import SimWhy from '../components/SimWhy';   // [D-700/701] пояснення червоної схожості - спільне з карткою учня партнера
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, Button, TextField, CircularProgress, List, ListItem, ListItemText, IconButton, Tooltip, Collapse, Chip, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import api, { getUser, updateTrialUser, getUserActivity, deleteUserActivity, deleteSessionActivity, sendMessageToUser, getUserCostsByDate } from '../api';
import ChatTab from './ChatTab';
import UserStudioPhone from '../components/UserStudioPhone';   // [D-560] функції юзера + зафіксований телефон-прев'ю Студії
import UserTestFlag from '../components/UserTestFlag';   // [D-593] «🧪 Тестовий»
import UserAppClicks from '../components/UserAppClicks';   // [D-768] переходи цього юзера в наші застосунки
import UserThemesGiven from '../components/UserThemesGiven';   // [D-771] теми, дані юзеру особисто

const actionIcon = { video_received:'🎬', mode_selected:'⚡', subtitles_configured:'💬', ad_configured:'📢', overlays_configured:'🖼', hashtags_selected:'🏷', processing_started:'🔄', processing_completed:'🏁', processing_incident:'🚨', version_confirmed:'✅', version_deleted:'🗑', payment_made:'💳' };
const actionLabel = { video_received:'Відео отримано', mode_selected:'Режим обрано', subtitles_configured:'Субтитри', ad_configured:'Реклама', overlays_configured:'Оверлеї', hashtags_selected:'Хештеги', processing_started:'Обробка запущена', processing_completed:'Обробка завершена', processing_incident:'🚨 Інцидент', version_confirmed:'Версія підтверджена', version_deleted:'Версія видалена', payment_made:'Оплата' };
// D-203: єдиний лейбл режиму (вкл. PROF) — використовується у шапці й деталях, щоб не було розбіжностей
const modeLabel = (m) => ({ standard:'🎬 Стандартна', instant:'⚡ Миттєва', prof:'⚙️ Професійна' }[m] || m);
// D-204: метадані сервера обробки → колір рамки/бейджа у звіті (staging + prod-воркери)
const serverMeta = (s) => ({
  // Звірено 19.08.2026: worker-1 більше не існує (сервер видалено 10.07),
  // натомість зʼявились worker-5 і netcup-1. Раніше вони потрапляли у запасну
  // сіру гілку нижче — працювало, але без свого кольору.
  'n8n-server': { label:'🖥 n8n-server', bg:'#818cf8', tc:'#fff' },
  'worker-2':   { label:'🖥 worker-2',   bg:'#fb923c', tc:'#231000' },
  'worker-5':   { label:'🖥 worker-5',   bg:'#facc15', tc:'#1c1600' },
  'staging':    { label:'🧪 STAGING',    bg:'#4ade80', tc:'#06210a' },
  'netcup-1':   { label:'🖥 netcup-1',   bg:'#9ca3af', tc:'#111' },
  // [D-731] обробка на пристрої юзера - застосунок на телефоні або ПК (сервер файл не отримує)
  'phone':      { label:'📱 Телефон',    bg:'#f472b6', tc:'#2a0617' },
  'pc':         { label:'💻 ПК',         bg:'#22d3ee', tc:'#04232a' },
}[s] || (s ? { label:'🖥 '+s, bg:'#607d8b', tc:'#fff' } : null));
const HIDDEN_ACTIONS = ['mode_selected'];

function groupSessions(activities) {
  const sessions = [];
  const devs = {};   // [D-731] обробки застосунків (origin 'device') - своя картка на кожну (за task_id), з ботом не змішуються
  let current = null;
  activities.forEach(a => {
    if (a.origin === 'device') {
      const k = (a.action_data && a.action_data.task_id) || a.id;
      if (!devs[k]) devs[k] = { start: null, actions: [], startTime: a.created_at, origin: 'device' };
      if (a.action_type === 'video_received') devs[k].start = a; else devs[k].actions.push(a);
      return;
    }
    if (a.action_type === 'video_received') {
      current = { start: a, actions: [], startTime: a.created_at, origin: a.origin };
      sessions.push(current);
    } else if (current) {
      current.actions.push(a);
    } else {
      if (!sessions.length) { current = { start: null, actions: [], startTime: a.created_at, origin: a.origin }; sessions.push(current); }
      sessions[0].actions.push(a);
    }
  });
  Object.values(devs).forEach(d => sessions.push(d));
  sessions.sort((x, y) => String(x.startTime || '').localeCompare(String(y.startTime || '')));
  return sessions;
}

// [D-570/571] замір схожості версії з оригіналом: менше - краще (до 20% зелене, 20-50% жовте, від 50% червоне); 💪 - сильна унікалізація
const simCol = (v) => (v >= 50 ? '#ff6b6b' : v >= 20 ? '#ffb74d' : '#4caf50');
const SimCell = ({ s }) => s ? <span title={'медіана ' + s.median_bits + ' біт · кадрів ' + s.frames} style={{ color: simCol(s.same_pct), fontWeight: 'bold' }}>{Math.round(s.same_pct)}%{s.strong ? ' 💪' : ''}</span> : <span style={{ color: '#555' }}>—</span>;
// заміри цієї сесії: від старту обробки до її завершення (+3 хв - замір іде окремим потоком), остання на кожен номер версії
function sessionSims(simRows, session) {
  if (!simRows || !simRows.length) return null;
  const ps = session.actions.find(a => a.action_type === 'processing_started'), pc = session.actions.find(a => a.action_type === 'processing_completed');
  if (session.origin === 'device') {   // [D-731] заміри цієї обробки застосунку сервер уже зібрав (_sim_ids)
    const ids = (ps && ps.action_data && ps.action_data._sim_ids) || [], got = {};
    simRows.forEach(r => { if (ids.includes(r.id) && (!got[r.ver] || r.id > got[r.ver].id)) got[r.ver] = r; });
    return Object.keys(got).length ? got : null;
  }
  const t0 = ps && ps.created_at ? new Date(ps.created_at).getTime() - 60000 : (session.start && session.start.created_at ? new Date(session.start.created_at).getTime() : null);
  if (t0 == null) return null;
  const t1 = (pc && pc.created_at ? new Date(pc.created_at).getTime() : t0 + 3 * 3600000) + 180000;
  const out = {};
  simRows.forEach(r => { if (Number(r.order_salt) < 0) return; const t = new Date(r.created_at).getTime(); if (t >= t0 && t <= t1 && (!out[r.ver] || r.id > out[r.ver].id)) out[r.ver] = r; });
  return Object.keys(out).length ? out : null;
}

// [D-603] «як пораховано»: журнал версій (version_ledger, D-602) і пояснення КАЛЬКУЛЯТОРА (bot/calc.py) - усе рахує сервер, тут лише показ
const dubT = (iso) => iso ? new Date(iso).toLocaleTimeString('uk-UA', { timeZone: 'Europe/Dublin', hour: '2-digit', minute: '2-digit' }) : '';
const dubDT = (iso) => iso ? new Date(iso).toLocaleString('uk-UA', { timeZone: 'Europe/Dublin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
function VersionsExplain({ v, title }) {
  if (!v) return null;
  const e = v.explain || {};
  const since = v.ledger_since ? String(v.ledger_since).slice(0, 10) : null;
  return (
    <Box sx={{ mt: 1, p: 1.2, border: '1px solid #2a3a4e', borderRadius: 1, bgcolor: '#0f0f1a' }}>
      <Typography variant="caption" sx={{ color: '#9a86ff', fontWeight: 'bold', display: 'block' }}>{title || ('🧮 Ліміт версій за добу ' + v.day + ' - як пораховано')}</Typography>
      <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 0.5 }}>Доба ліміту - за Києвом: {dubDT(v.day_start)} - {dubDT(v.day_end)} за вашим часом. Рахує калькулятор із журналу версій; журнал не можна змінити заднім числом.</Typography>
      <Typography sx={{ color: e.chain_ok === false ? '#ff6b6b' : '#fff', fontSize: 13, fontWeight: 'bold' }}>{e.text || '—'}</Typography>
      {v.day === v.today && v.match !== null && <Typography variant="caption" sx={{ color: v.match ? '#4caf50' : '#ff6b6b', display: 'block' }}>
        {v.match ? '✓' : '⚠️'} Лічильник зараз {v.counter} {v.match ? '=' : '≠'} сума журналу {v.ledger_sum} · наступне скидання {dubDT(v.next_reset)}</Typography>}
      {v.rows && v.rows.length > 0 && <Box sx={{ overflowX: 'auto' }}><Box component="table" sx={{ mt: 0.6, borderCollapse: 'collapse', fontSize: 11, fontFamily: 'monospace' }}>
        <Box component="thead"><Box component="tr">{['Час', 'Що', 'Зміна', 'Використано: було → стало', 'Обробка', 'Хто'].map(h => <Box component="th" key={h} sx={{ color: '#9a86ff', textAlign: 'left', px: 0.8, py: 0.2, borderBottom: '1px solid #333', whiteSpace: 'nowrap' }}>{h}</Box>)}</Box></Box>
        <Box component="tbody">{v.rows.map(r => (
          <Box component="tr" key={r.id} title={r.note || ''} sx={{ '&:nth-of-type(even)': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
            <Box component="td" sx={{ color: '#777', px: 0.8, whiteSpace: 'nowrap' }}>{dubT(r.created_at)}</Box>
            <Box component="td" sx={{ color: '#ddd', px: 0.8 }}>{r.label}{r.note ? ' ℹ️' : ''}</Box>
            <Box component="td" sx={{ color: r.delta > 0 ? '#ff8a65' : '#4caf50', px: 0.8, fontWeight: 'bold', textAlign: 'right' }}>{r.delta > 0 ? '+' : ''}{r.delta}</Box>
            <Box component="td" sx={{ color: '#cfc8cc', px: 0.8, whiteSpace: 'nowrap' }}>{r.used_before} → {r.used_after}</Box>
            <Box component="td" sx={{ color: '#777', px: 0.8 }}>{r.task_id ? String(r.task_id).slice(0, 8) : '—'}</Box>
            <Box component="td" sx={{ color: '#777', px: 0.8, whiteSpace: 'nowrap' }}>{String(r.source || '').split('@')[0]}</Box>
          </Box>))}</Box>
      </Box></Box>}
      {since && v.day < since && <Typography variant="caption" sx={{ color: '#777', display: 'block', mt: 0.5 }}>Журнал версій ведеться з {dubDT(v.ledger_since)} - за раніші дні записів немає.</Typography>}
    </Box>
  );
}

// [D-608] «як пораховано» строки доступу і гроші: журнали access_ledger / money_ledger (D-607) + пояснення КАЛЬКУЛЯТОРА (calc.explain_access,
// explain_payments, explain_referral). Усе рахує сервер, тут лише показ. Дати строків - за Києвом (як рахує калькулятор), час подій - за вашим.
const kyD = (iso) => iso ? new Date(iso).toLocaleDateString('uk-UA', { timeZone: 'Europe/Kyiv' }) : '—';
const eur = (v) => (v === null || v === undefined || v === '') ? '—' : Number(v).toFixed(2);
const HowTable = ({ head, rows }) => (
  <Box sx={{ overflowX: 'auto' }}><Box component="table" sx={{ mt: 0.6, borderCollapse: 'collapse', fontSize: 11, fontFamily: 'monospace' }}>
    <Box component="thead"><Box component="tr">{head.map(h => <Box component="th" key={h} sx={{ color: '#9a86ff', textAlign: 'left', px: 0.8, py: 0.2, borderBottom: '1px solid #333', whiteSpace: 'nowrap' }}>{h}</Box>)}</Box></Box>
    <Box component="tbody">{rows.map((r, i) => (
      <Box component="tr" key={i} sx={{ '&:nth-of-type(even)': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
        {r.map((c, j) => <Box component="td" key={j} sx={{ color: j === 0 ? '#777' : '#ddd', px: 0.8, whiteSpace: 'nowrap' }}>{c}</Box>)}
      </Box>))}</Box>
  </Box></Box>
);
const HowBox = ({ title, children }) => (
  <Box sx={{ mt: 1, p: 1.2, border: '1px solid #2a3a4e', borderRadius: 1, bgcolor: '#0f0f1a' }}>
    <Typography variant="caption" sx={{ color: '#9a86ff', fontWeight: 'bold', display: 'block', mb: 0.5 }}>{title}</Typography>
    {children}
  </Box>
);

function AccessExplain({ a, field }) {
  if (!a || !a.fields || !a.fields[field]) return <Typography sx={{ color: '#777' }}>Немає даних журналу</Typography>;
  const x = a.fields[field];
  const steps = x.steps || [];
  return (
    <HowBox title={'🧮 ' + x.label + ' - як пораховано'}>
      <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 0.5 }}>Строк рахує калькулятор (календарні місяці й дні за Києвом) із журналу строків; журнал не можна змінити заднім числом.</Typography>
      <Typography sx={{ color: x.chain_ok === false ? '#ff6b6b' : '#fff', fontSize: 13, fontWeight: 'bold' }}>{x.text || '—'}</Typography>
      <Typography variant="caption" sx={{ color: x.match ? '#4caf50' : '#ff6b6b', display: 'block' }}>
        {x.match ? '✓ Строк у базі = останній запис журналу' : '⚠️ Строк у базі (' + kyD(x.db_value) + ') ≠ журнал (' + kyD(x.end) + ') - перевірити'}</Typography>
      {steps.length > 0 && <HowTable head={['Коли', 'Що', 'Зміна', 'Було → стало (Київ)', 'Що саме', 'Хто']}
        rows={steps.map(s => [dubDT(s.at), s.label, s.change, (s.before ? kyD(s.before) : '—') + ' → ' + (s.after ? kyD(s.after) : '—'), s.ref || '—', s.who || '—'])} />}
      {a.ledger_since && <Typography variant="caption" sx={{ color: '#777', display: 'block', mt: 0.5 }}>Журнал строків ведеться з {dubDT(a.ledger_since)}; що було раніше - видно з оплат нижче.</Typography>}
      {field !== 'trial_end' && (a.payments || []).length > 0 && <>
        <Typography variant="caption" sx={{ color: '#b39ddb', display: 'block', mt: 1 }}>Оплати юзера (довідка)</Typography>
        <HowTable head={['Створено', '№', 'Пакет', 'Міс', 'Сума', 'Стан', 'Хто підтвердив']}
          rows={a.payments.filter(p => field === 'pcapp_until' ? p.kind === 'pcapp' : (p.kind !== 'pcapp' && p.kind !== 'tokens')).slice(0, 20).map(p => [kyD(p.created_at), p.id, p.package || '—', p.months || '—',
            p.amount != null ? eur(p.amount) + ' ' + (p.currency || '') : '—', p.confirmed ? '✅' : '⏳', p.confirmed_by || '—'])} /></>}
      {field === 'subscription_end' && (a.partner_sales || []).length > 0 && <>
        <Typography variant="caption" sx={{ color: '#b39ddb', display: 'block', mt: 1 }}>Оплати через партнера</Typography>
        <HowTable head={['Рішення', '№', 'Пакет', 'Міс', 'Сума', 'Стан', 'Період (Київ)']}
          rows={a.partner_sales.map(s => [kyD(s.decided_at), s.id, s.package_name || '—', s.months, eur(s.amount) + ' ' + (s.ccy || ''), s.status,
            (s.access_from ? kyD(s.access_from) : '?') + ' → ' + kyD(s.access_until)])} /></>}
    </HowBox>
  );
}

function MoneyExplain({ m }) {
  if (!m) return <Typography sx={{ color: '#777' }}>Немає даних</Typography>;
  const r = m.referral;
  const st = { confirmed: '✅ розморожено', frozen: '🧊 заморожено', blocked: '⛔ захист маржі', void: '✖ скасовано', completed: '✅ виплачено', pending: '⏳ чекає', rejected: '✖ відхилено' };
  return (<>
    <HowBox title="🧮 Оплати юзера - як пораховано">
      <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 'bold' }}>{m.payments ? m.payments.text : '—'}</Typography>
      {(m.payment_rows || []).length > 0 && <HowTable head={['Створено', '№', 'Пакет', 'Сума', 'Стан', 'Хто підтвердив']}
        rows={m.payment_rows.slice(0, 20).map(p => [kyD(p.created_at), p.id, p.package || '—', p.amount != null ? eur(p.amount) + ' ' + (p.currency || '') : '—', p.confirmed ? '✅' : '⏳', p.confirmed_by || '—'])} />}
    </HowBox>
    {r && <HowBox title="🧮 Реферальний баланс - з чого складається">
      <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 0.5 }}>Калькулятор складає баланс із нарахувань і виведень і звіряє з тим, що записано на балансі.</Typography>
      {(r.lines || []).map((l, i) => <Typography key={i} sx={{ color: l.startsWith('⚠️') ? '#ff6b6b' : '#fff', fontSize: 13, fontWeight: i === 2 ? 'bold' : 'normal' }}>{l}</Typography>)}
      <HowTable head={['', 'На балансі', 'З записів', '']} rows={(r.checks || []).map(c => [c.label, eur(c.actual), eur(c.expected), c.ok ? '✓' : '⚠️ ' + eur(c.diff)])} />
      {(m.payouts || []).length > 0 && <>
        <Typography variant="caption" sx={{ color: '#b39ddb', display: 'block', mt: 1 }}>Нарахування ({m.payouts.length})</Typography>
        <HowTable head={['Коли', 'Від кого', 'Рівень', 'Сума €', 'Стан', 'Оплата']}
          rows={m.payouts.slice(0, 30).map(p => [kyD(p.created_at), '@' + (p.payer || p.payer_user_id), 'L' + p.level, eur(p.amount), st[p.status] || p.status, p.payment_id ? '№' + p.payment_id : '—'])} /></>}
      {((m.withdrawals || []).length > 0 || (m.deleted_withdrawals || []).length > 0) && <>
        <Typography variant="caption" sx={{ color: '#b39ddb', display: 'block', mt: 1 }}>Виведення</Typography>
        <HowTable head={['Коли', '№', 'Сума €', 'Стан']}
          rows={[...(m.withdrawals || []).map(w => [kyD(w.completed_at || w.requested_at), w.id, eur(w.amount), st[w.status] || w.status]),
                 ...(m.deleted_withdrawals || []).map(w => [kyD(w.at), w.id, eur(w.amount), '✅ виплачено, запис заявки видалено'])]} /></>}
    </HowBox>}
    <HowBox title="📒 Журнал грошей цього юзера">
      {(m.ledger || []).length > 0
        ? <HowTable head={['Коли', 'Що', 'Де', 'Зміна', 'Стан', 'Що саме', 'Хто']}
            rows={m.ledger.map(l => [dubDT(l.created_at), l.label, l.tbl + ' №' + l.row_id, (Number(l.delta) > 0 ? '+' : '') + eur(l.delta) + ' ' + (l.ccy || ''),
              (l.state_before || '—') + ' → ' + (l.state_after || '—'), l.ref || '—', String(l.source || '').split('@')[0]])} />
        : <Typography variant="caption" sx={{ color: '#777' }}>Змін після запуску журналу ще не було{m.ledger_since ? ' (журнал з ' + dubDT(m.ledger_since) + ')' : ''}.</Typography>}
    </HowBox>
  </>);
}

function UniqTable({ uniq, margins, sims }) {
  const mMap = {};
  if (margins) String(margins).split(' | ').forEach(p => { const m = p.match(/^(v\d+):\s*(.+)$/); if (m) mMap[m[1]] = m[2].trim(); });
  const merged = [];
  String(uniq || '').split(' | ').forEach(seg => {
    seg = seg.trim();
    if (/^v\d+:/.test(seg)) merged.push(seg);
    else if (merged.length) merged[merged.length - 1] += ' | ' + seg;
  });
  const rows = merged.map(x => {
    const g = (re) => { const y = x.match(re); return y ? y[1] : '—'; };
    const label = (x.match(/^v\d+/) || ['?'])[0];
    return { sim: sims ? <SimCell s={sims[Number(label.slice(1))]} /> : null, v: label, bright: g(/яскр:([+-]?[\d.]+)/), contrast: g(/конт:([+-]?[\d.]+)/), noise: g(/шум:([+-]?[\d.]+)/), rot: g(/рот:([+-]?[\d.]+)/), speed: g(/швид:([+-]?[\d.]+)/), title: /T:on/.test(x) ? '✓' : (/T:off/.test(x) ? '—' : ''), margin: mMap[label] || '' };
  });
  if (!rows.length) return null;
  const cols = [['v','Вер','#f5c84c'],['bright','Яскр'],['contrast','Конт'],['noise','Шум'],['rot','Поворот'],['speed','Швидк'],['title','Тайтл']];
  if (rows.some(r => r.margin)) cols.push(['margin','Margin суб']);
  if (sims) cols.splice(1, 0, ['sim', '🔬 Схожість']);
  return (
    <Box sx={{ mt: 0.7, mb: 0.3, overflowX: 'auto' }}>
      <Typography variant="caption" sx={{ color:'var(--ac1, #7c4dff)', display:'block', mb:0.3, fontWeight:'bold' }}>🔧 Унікалізація по версіях</Typography>
      <Box component="table" sx={{ borderCollapse:'collapse', fontFamily:'monospace', fontSize:11 }}>
        <Box component="thead"><Box component="tr">
          {cols.map(c => <Box component="th" key={c[0]} sx={{ color:'#9a86ff', textAlign:'right', px:0.9, py:0.25, borderBottom:'1px solid #333', whiteSpace:'nowrap' }}>{c[1]}</Box>)}
        </Box></Box>
        <Box component="tbody">
          {rows.map((r,i) => (
            <Box component="tr" key={i} sx={{ '&:nth-of-type(even)': { bgcolor:'rgba(255,255,255,0.03)' } }}>
              {cols.map(c => <Box component="td" key={c[0]} sx={{ color: c[2] || '#cfc8cc', textAlign:'right', px:0.9, py:0.25, whiteSpace:'nowrap', fontWeight: c[0]==='v'?'bold':'normal' }}>{r[c[0]]}</Box>)}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function SessionCard({ session, idx, onDelete, simRows, vTask }) {
  const [open, setOpen] = useState(false);
  const sims = sessionSims(simRows, session);   // [D-571]
  const simMax = sims ? Math.max(...Object.values(sims).map(s => s.same_pct)) : null;
  const start = session.start;
  const data = start?.action_data || {};
  const mode = session.actions.find(a => a.action_type === 'mode_selected')?.action_data || {};
  const proc = session.actions.find(a => a.action_type === 'processing_started')?.action_data || {};
  const time = start?.created_at ? new Date(start.created_at).toLocaleTimeString('uk-UA', {timeZone:'Europe/Dublin', hour:'2-digit', minute:'2-digit'}) : '?';
  // D-203: достовірний режим/версії — mode_selected або fallback на реальний processing_started (бот сам це записав)
  const modeVal = mode.mode || proc.mode;
  const versVal = mode.versions || proc.versions;
  const isStaging = session.origin === 'staging';
  const isDevice = session.origin === 'device';   // [D-731] 📱 / 💻
  const _pc = session.actions.find(a => a.action_type === 'processing_completed')?.action_data || {};
  const _psAct = session.actions.find(a => a.action_type === 'processing_started');
  const _pcAct = session.actions.find(a => a.action_type === 'processing_completed');
  const _procSec = (_psAct && _psAct.created_at && _pcAct && _pcAct.created_at) ? Math.max(0, Math.round((new Date(_pcAct.created_at) - new Date(_psAct.created_at))/1000)) : null;
  const _procFmt = _procSec != null ? (_procSec >= 60 ? (Math.floor(_procSec/60) + 'хв ' + (_procSec%60) + 'с') : (_procSec + 'с')) : null;
  const _vt = vTask && _psAct && _psAct.action_data && _psAct.action_data.task_id ? vTask[_psAct.action_data.task_id] : null;   // [D-603] журнал версій цієї обробки
  const _inc = session.actions.find(a => a.action_type === 'processing_incident')?.action_data;
  // D-204/D-205/D-206: рамка+бейдж за сервером; інцидент (cancel/timeout) → червоний 🚨 + сервер у бейджі
  const meta = isStaging ? serverMeta('staging') : isDevice ? serverMeta(proc.server) : (_inc ? { label: '🚨 Інцидент' + (_inc.server ? ' · 🖥 ' + _inc.server : ''), bg:'#f44336', tc:'#fff' } : serverMeta(_pc.server));
  const accent = meta ? meta.bg : '#333';

  return (
    <Paper sx={{ bgcolor: '#0f0f1a', border: '1px solid ' + accent, mb: 1, borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <Typography sx={{ color: meta ? meta.bg : 'var(--ac1, #7c4dff)', mr: 1 }}>🎬</Typography>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Відео #{idx+1} — {time}
            {meta ? <Box component="span" sx={{ ml: 1, px: 0.7, py: 0.1, bgcolor:meta.bg, color:meta.tc, borderRadius: 1, fontSize: 10, fontWeight:'bold', verticalAlign:'middle' }}>{meta.label}</Box> : null}
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {data.duration_sec ? data.duration_sec + 'с' : ''} {data.size_mb ? data.size_mb + 'МБ' : ''}
            {modeVal ? ' | ' + modeLabel(modeVal) : ''}
            {versVal ? ' | ' + versVal + ' верс.' : ''}
            {proc.has_ad ? ' | 📢' : ''} {proc.has_subtitles ? ' | 💬' : ''}
            {isDevice && !_pcAct && (proc._state === 'gone'
              ? <span style={{ color:'#ffb74d' }}>{' | ⚠️ без звісток, готово ' + (proc._done || 0) + ' з ' + (proc.versions || '?')}</span>
              : <span style={{ color:'#7ffcd3' }}>{' | ⏳ обробляє, готово ' + (proc._done || 0) + ' з ' + (proc.versions || '?')}</span>)}
          </Typography>
        </Box>
        {sims && <Typography title="Найбільша схожість версії з оригіналом (розгорніть - по кожній версії)" sx={{ color: simCol(simMax), fontWeight:'bold', fontSize:13, mr:1, whiteSpace:'nowrap' }}>🔬 {Math.round(simMax)}%</Typography>}
        {_vt && <Typography title={'Журнал версій цієї обробки:\n' + _vt.items.slice().reverse().map(i => i.label + ' ' + (i.delta > 0 ? '+' : '') + i.delta).join('\n')} sx={{ color:'#ffb74d', fontWeight:'bold', fontSize:12, mr:1, whiteSpace:'nowrap' }}>🧮 −{_vt.spent}{_vt.refunded ? ' · +' + _vt.refunded : ''}</Typography>}
        {_procFmt && <Typography sx={{ color:'#f44336', fontWeight:'bold', fontSize:13, mr:1, whiteSpace:'nowrap' }}>⏱ {_procFmt}</Typography>}
        <IconButton size="small" sx={{ color: meta ? meta.bg : 'var(--ac1, #7c4dff)' }}>
          {open ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
        </IconButton>
        {!isDevice && <IconButton size="small" onClick={e=>{e.stopPropagation();onDelete&&onDelete(session.start?.created_at, session.origin)}} sx={{color:"#f44336","&:hover":{color:"#ff1744"}}}><DeleteIcon fontSize="small"/></IconButton>}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2 }}>
          {sims && simMax >= 50 && <SimWhy sims={sims} />}
          {_vt && <Typography variant="caption" sx={{ color:'#ffb74d', display:'block', mb:0.6 }}>🧮 Ліміт версій за цю обробку: {_vt.items.slice().reverse().map(i => i.label + ' ' + (i.delta > 0 ? '+' : '') + i.delta + ' (' + dubT(i.at) + ')').join(' · ')}</Typography>}
          {session.actions.filter(a => !HIDDEN_ACTIONS.includes(a.action_type)).map((a, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'flex-start' }}>
              <Typography sx={{ color: 'var(--ac1, #7c4dff)', minWidth: 20, fontSize: 14 }}>{actionIcon[a.action_type] || '•'}</Typography>
              <Box>
                <Typography variant="caption" sx={{ color: '#ddd' }}>
                  {actionLabel[a.action_type] || a.action_type}
                  <span style={{ color: '#555', marginLeft: 6 }}>{a.created_at ? new Date(a.created_at).toLocaleTimeString('uk-UA', {timeZone:'Europe/Dublin', hour:'2-digit', minute:'2-digit', second:'2-digit'}) : ''}</span>
                </Typography>
                {a.action_data && Object.keys(a.action_data).length > 0 &&
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                    {Object.entries(a.action_data).filter(([k,v]) => {
                      if (isDevice && (k.startsWith('_') || k === 'task_id')) return false;   // [D-731] службове
                      if (a.action_type === 'processing_started' && ['instant','overlays','versions','task_id'].includes(k)) return false;
                      if (a.action_type === 'overlays_configured' && ['count','photos','videos'].includes(k)) return false;
                      if (a.action_type === 'processing_completed' && ['versions','cost_total_usd','uniq_versions','sub_margins'].includes(k)) return false;
                      if (k === 'show_in_ad' && !v) return false;
                      return v !== null && v !== undefined;
                    }).map(([k,v]) => {
                          if (k === 'duration_sec') return 'час: ' + Math.floor(v/60) + 'хв ' + parseFloat(v%60).toFixed(1) + 'с';
                          if (k === 'position') return 'position: ' + (v === 'bottom' ? '⬇️ Внизу' : v === 'top' ? '⬆️ Вгорі' : v);
                          if (k === 'size') {
                            const sizeMap = {'xs':'Малий (16px)','small':'Середній (18px)','medium':'Великий (20px)','large':'Дуже великий (24px)'};
                            return 'розмір: ' + (sizeMap[v] || v);
                          }
                          if (k === 'color') return 'колір: ' + v;
                          if (k === 'background') return 'фон: ' + (v === 'full' ? 'білий' : v === 'semi' ? 'напівпрозорий' : v === 'none' ? 'без фону' : v === 'black' ? 'чорний' : v);
                          if (k === 'translate') return 'переклад: ' + (v ? 'є' : 'немає');
                          if (k === 'has_subtitles') return v ? 'субтитри: є в основному відео' : 'субтитри: без субтитрів';
                          if (k === 'has_ad') return v ? 'реклама: є' : 'реклама: немає';
                          if (k === 'subs_in_ad' || k === 'show_in_ad') return v ? 'субтитри в рекламі: є' : 'субтитри в рекламі: немає';
                          if (k === 'cost_per_ver_usd') return 'вартість за версію: $' + parseFloat(v).toFixed(4);
                          if (k === 'mode') return 'режим: ' + modeLabel(v);
                          if (k === 'batch_fx_mode') return 'уніфікація пакету: ' + ({A:'🅰 Швидко', B:'🅱 Кожна унікальна', C:'🅲 5 варіантів'}[v] || v);
                          if (k === 'server' && (v === 'phone' || v === 'pc')) return serverMeta(v).label;   // [D-731]
                          if (k === 'server') return (v && v !== 'dashboard' && v !== '—') ? '🖥 ' + v : '🖥 воркер не зафіксовано (скасовано в черзі)';
                          if (k === 'ai_model') return '🤖 AI-модель: ' + v;
                          if (k === 'reason') return 'причина: ' + ({poll_timeout:'⏰ таймаут полінгу — задачу вбито', cancelled_admin:'🛑 скасовано адміном'}[v] || v);
                          if (k === 'versions_total') return 'версій створено: ' + v;
                          if (k === 'versions_delivered') return 'доставлено: ' + v;
                          if (k === 'versions_refunded') return 'повернено квоти: ' + v;
                          if (k === 'incident') return 'інцидент #' + v;
                          if (k === 'uniq_versions') {
                            const lines = v.split(' | ');
                            return lines.map((l,i) => (i===0?'🔧 ':'      ')+l).join('\n');
                          }
                          if (k === 'details') return v;
                          if (k === 'sub_margins') return 'margin субтитрів: ' + v;
                          if (k === 'ai_cost_usd') return 'вартість AI: $' + parseFloat(v).toFixed(4);
                          if (k === 'has_subtitles') return v ? 'субтитри: присутні в основному відео' : 'субтитри: без субтитрів';
                          if (k === 'has_ad') return v ? 'реклама: з рекламою' : 'реклама: без реклами';
                          if (k === 'subs_in_ad') return v ? 'субтитри в рекламі: є' : 'субтитри в рекламі: немає';
                          if (k === 'cost_total_usd') return 'загальна вартість: $' + parseFloat(v).toFixed(4);
                          if (k === 'cost_per_ver_usd') return 'за версію: $' + parseFloat(v).toFixed(4);
                          return k + ': ' + v;
                        }).join(' | ')}
                  </Typography>
                }
                {a.action_type === 'processing_completed' && a.action_data && a.action_data.uniq_versions &&
                  <UniqTable uniq={a.action_data.uniq_versions} margins={a.action_data.sub_margins} sims={sims} />}
                {a.action_type === 'processing_completed' && sims && !(a.action_data && a.action_data.uniq_versions) && (   /* [D-571] таблиці версій немає - заміри окремим рядком */
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1, fontSize: 12 }}>
                    <span style={{ color: '#9a86ff', fontWeight: 'bold' }}>🔬 Схожість з оригіналом:</span>
                    {Object.keys(sims).sort((x, y) => x - y).map(k => <span key={k}><b style={{ color: '#f5c84c' }}>v{k}</b> <SimCell s={sims[k]} /></span>)}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

// [D-678] OWNER 19.10: «відкриваючи кожного юзера у мене були кнопки v-1/v-2/v-3/v-4/v-5 і ті версії які вже показуються юзеру,
// то щоб вони вже були ввімкнуті». Рахує та сама функція, що в боті (bot/versions.py).
// [D-679] Картка = перемикач: натиск → вікно «що зміниться» (сервер робить ті самі записи на копії й відкочує; гроші - калькулятор) →
// «Відкрити / Сховати цьому юзеру» або «↩ як усім». Змінюються лише кнопки; гроші, строки, ліміт і токени - ні (правило R32).
const VER_OWN = { open: 'ви відкрили особисто', closed: 'ви сховали особисто', mixed: 'частину кнопок задано особисто' };
/* [D-804] OWNER 01.11: «бажано мені в дашборд зробити можливість щоб я міг власноруч редагувати… якому юзеру…» - найдовше СВОЄ відео фону
   теми «🖼 Рамка+фон»; порожньо - правило «не довше за основне відео в півтора раза» */
function BgvidLimit({ userId }) {
  const [sec, setSec] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => { if (!userId) return; api.get(`/users/${userId}/bgvid-limit`).then(r => setSec(String((r.data && r.data.sec) || ''))).catch(() => {}); }, [userId]);
  const save = () => api.post(`/users/${userId}/bgvid-limit?sec=${Number(sec) || 0}`).then(() => { setNote('✅ Збережено'); setTimeout(() => setNote(''), 2500); })
    .catch(e => setNote('❌ ' + ((e.response && e.response.data && e.response.data.detail) || 'помилка')));
  return (
    <Box data-bgvid-limit="1" sx={{ mt: 1.5, pt: 1.2, borderTop: '1px dashed #333' }}>
      <Typography variant="caption" sx={{ color: '#b39ddb', display: 'block' }}>🧠 Своє відео фону «Рамка+фон», сек</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: .5 }}>
        <TextField type="number" size="small" value={sec} onChange={e => setSec(e.target.value)} placeholder="1,5×"
          inputProps={{ min: 0, style: { color: '#fff', width: 70 } }} sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }} />
        <Button variant="outlined" onClick={save} sx={{ color: '#b39ddb', borderColor: '#b39ddb' }}>Зберегти</Button>
        {note && <Typography variant="caption" sx={{ color: '#ccc' }}>{note}</Typography>}
      </Box>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: .5 }}>0 / порожньо - не довше за основне відео в 1,5 раза</Typography>
    </Box>
  );
}

/* [D-816] OWNER 05.11: «…і конкретно кожному, якщо вирішу, що комусь хочу надати більше або менше обмеження» - «✨ Домалювати»,
   секунди тексту в ролику; сильніше за партнера і за загальне (рахує calc.detext_fill_max_sec на сервері) */
const DFILL_SRC = { user: 'особисто цьому юзеру', partner: 'від партнера', global: 'як усім' };
function DetextFillLimit({ userId }) {
  const [sec, setSec] = useState('');
  const [eff, setEff] = useState(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!userId) return;
    api.get(`/users/${userId}/detext-fill-limit`).then(r => { setSec(String((r.data && r.data.sec) || '')); setEff((r.data && r.data.eff) || null); }).catch(() => {});
  }, [userId]);
  const save = () => api.post(`/users/${userId}/detext-fill-limit?sec=${Math.max(0, Math.round(Number(sec) || 0))}`)
    .then(r => { setEff((r.data && r.data.eff) || null); setNote('✅ Збережено'); setTimeout(() => setNote(''), 2500); })
    .catch(e => setNote('❌ ' + ((e.response && e.response.data && e.response.data.detail) || 'помилка')));
  return (
    <Box data-dfill-limit="1" sx={{ mt: 1.5, pt: 1.2, borderTop: '1px dashed #333' }}>
      <Typography variant="caption" sx={{ color: '#c4a7ff', display: 'block' }}>✨ Домалювати: найбільше секунд тексту в ролику</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: .5 }}>
        <TextField type="number" size="small" value={sec} onChange={e => setSec(e.target.value)} placeholder="як усім"
          inputProps={{ min: 0, style: { color: '#fff', width: 70 } }} sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }} />
        <Button variant="outlined" onClick={save} sx={{ color: '#c4a7ff', borderColor: '#c4a7ff' }}>Зберегти</Button>
        {note && <Typography variant="caption" sx={{ color: '#ccc' }}>{note}</Typography>}
      </Box>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: .5 }}>
        {eff ? `Зараз діє: ${eff.sec} с - ${DFILL_SRC[eff.src] || eff.src}. ` : ''}0 / порожньо - як партнер або як усім
      </Typography>
    </Box>
  );
}

function VersionsBlock({ userId, onChanged }) {
  const [st, setSt] = useState(null);
  const [dlg, setDlg] = useState(null);      // { r, to, pv, busy, err }
  const [note, setNote] = useState('');
  const load = () => api.get(`/users/${userId}/versions`).then(r => setSt(r.data || false)).catch(() => setSt(false));
  useEffect(() => { if (userId) load(); }, [userId]);
  const preview = (r, to) => {
    setDlg({ r, to, pv: null, busy: true, err: '' });
    api.get(`/users/${userId}/versions/preview`, { params: { v: r.key, to } })
      .then(x => setDlg({ r, to, pv: x.data, busy: false, err: '' }))
      .catch(e => setDlg({ r, to, pv: null, busy: false, err: (e.response && e.response.data && e.response.data.detail) || 'Не вдалося порахувати' }));
  };
  const apply = () => {
    if (!dlg || !dlg.pv) return;
    setDlg({ ...dlg, busy: true });
    api.post(`/users/${userId}/versions`, null, { params: { v: dlg.r.key, to: dlg.to, sig: dlg.pv.sig } })
      .then(x => { setSt(x.data.state); setDlg(null);
        setNote('✅ Збережено' + (x.data.changed && x.data.changed.length ? ': змінилось ' + x.data.changed.join(', ') : ' (що бачить юзер - не змінилось)'));
        setTimeout(() => setNote(''), 6000); onChanged && onChanged(); })
      .catch(e => setDlg({ ...dlg, busy: false, err: (e.response && e.response.data && e.response.data.detail) || 'Не збережено' }));
  };
  if (st === null) return null;
  if (!st || !Array.isArray(st.rows)) return (
    <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #553', p: 1.5, mb: 3 }}>
      <Typography sx={{ color: '#fb8', fontSize: 13 }}>🧭 Версії: не вдалося прочитати стан</Typography>
    </Paper>
  );
  const p = st.partner;
  const pv = dlg && dlg.pv;
  const aft = pv ? Object.fromEntries(pv.after.rows.map(x => [x.key, x])) : {};
  return (
    <Paper id="ud-versions" sx={{ bgcolor: '#151a2c', border: '1px solid rgba(var(--ac1rgb, 124, 77, 255),.5)', p: { xs: 1.5, sm: 2 }, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1.2 }}>
        <Typography variant="subtitle1" sx={{ color: 'var(--ac2, #a78bfa)', fontWeight: 'bold' }}>🧭 Версії - що бачить юзер</Typography>
        <Typography sx={{ fontSize: 11.5, color: '#8fa3b8', flex: 1, minWidth: 200 }}>
          ✅ - бот показує цю версію, ⛔ - ні. Натисніть картку - покажу, що зміниться, і лише потім збережу. Гроші й токени перемикач не чіпає.
        </Typography>
      </Box>
      {note && <Typography sx={{ color: '#7ffcd3', fontSize: 13, mb: 1 }}>{note}</Typography>}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1 }}>
        {st.rows.map(r => (
          <Box key={r.key} role="button" title={r.where} onClick={() => preview(r, r.on ? 'off' : 'on')}
            sx={{ p: 1.2, borderRadius: 1.5, minWidth: 0, cursor: 'pointer',
              bgcolor: r.on ? 'rgba(0,229,160,.09)' : 'rgba(255,255,255,.03)',
              border: '1px solid ' + (r.on ? 'rgba(0,229,160,.55)' : '#3a3a48'), '&:hover': { borderColor: 'var(--ac2, #a78bfa)' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: 16, color: r.on ? '#7ffcd3' : '#9aa3b0' }}>{r.v}</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 'bold', color: r.on ? '#7ffcd3' : '#ff8a80', whiteSpace: 'nowrap' }}>{r.on ? '✅ бачить' : '⛔ ні'}</Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: '#e6e6f0', mt: 0.3 }}>{r.title}</Typography>
            <Typography sx={{ fontSize: 11.5, color: '#aab4c2', mt: 0.5, lineHeight: 1.35, wordBreak: 'break-word' }}>{r.why}</Typography>
            {r.own !== 'inherit' && <Typography sx={{ fontSize: 11, color: '#ffd27f', mt: 0.4 }}>✋ {VER_OWN[r.own] || r.own}</Typography>}
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: '#8fa3b8', mt: 1.2 }}>
        «✨ Зі спецефектами» зараз: режим {st.mode_title}
        {p ? ' · партнер «' + p.name + '»' + (p.hyper_on ? ' з «Посиленням»' : '') + (p.active ? '' : ' (вимкнений)') : ' · без партнера'}
        {st.owner ? ' · це ваш акаунт' : ''}
      </Typography>

      <Dialog open={!!dlg} onClose={() => !(dlg && dlg.busy) && setDlg(null)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { bgcolor: '#151a2c', color: '#e6e6f0', border: '1px solid rgba(var(--ac1rgb, 124, 77, 255),.5)', m: { xs: 1.5, sm: 4 }, width: { xs: 'calc(100% - 24px)', sm: undefined } } }}>
        {dlg && <>
          <DialogTitle sx={{ fontSize: 17, pb: 1 }}>
            {dlg.r.v} {dlg.r.title}: {dlg.to === 'on' ? 'відкрити цьому юзеру' : dlg.to === 'off' ? 'сховати цьому юзеру' : 'як усім'}
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 1.5 }}>
              {[['on', '✅ Відкрити'], ['off', '⛔ Сховати'], ['inherit', '↩ Як усім']].map(([k, l]) => (
                <Button key={k} size="small" disabled={dlg.busy} onClick={() => preview(dlg.r, k)} variant={dlg.to === k ? 'contained' : 'outlined'}
                  sx={{ textTransform: 'none', fontSize: 12.5, ...(dlg.to === k ? { bgcolor: 'var(--ac1, #7c4dff)' } : { color: '#c7b8ff', borderColor: '#4a4170' }) }}>{l}</Button>
              ))}
            </Box>
            {dlg.busy && !pv && <CircularProgress size={22} sx={{ color: 'var(--ac1, #7c4dff)' }} />}
            {pv && <>
              <Typography sx={{ fontSize: 12.5, color: '#8fa3b8', mb: 0.6 }}>Що бачитиме юзер після збереження:</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 0.6, mb: 1.5 }}>
                {pv.before.rows.map(b => { const a = aft[b.key] || b; const ch = a.on !== b.on; return (
                  <Box key={b.key} sx={{ p: 0.8, borderRadius: 1, border: '1px solid ' + (ch ? '#ffd27f' : '#333'), bgcolor: ch ? 'rgba(255,210,127,.08)' : 'transparent' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: '#e6e6f0' }}>{b.v}</Typography>
                    <Typography sx={{ fontSize: 12, color: ch ? '#ffd27f' : '#8fa3b8' }}>{b.on ? '✅' : '⛔'} → {a.on ? '✅' : '⛔'}{ch ? ' зміна' : ''}</Typography>
                  </Box>); })}
              </Box>
              {aft[dlg.r.key] && <Typography sx={{ fontSize: 12.5, color: '#aab4c2', mb: 1 }}>Чому так буде: {aft[dlg.r.key].why}</Typography>}
              {pv.pay && <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'rgba(124,77,255,.10)', border: '1px solid #4a4170', mb: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: '#c7b8ff' }}>🧮 Оплата (рахує калькулятор)</Typography>
                <Typography sx={{ fontSize: 13, color: pv.pay.short ? '#ffb74d' : '#e6e6f0' }}>{pv.pay.text}</Typography>
              </Box>}
              <Typography sx={{ fontSize: 11.5, color: '#8fa3b8' }}>
                Змінюються лише кнопки. Списань, нарахувань, строків і лімітів перемикач не робить; зміна йде в журнал (хто, коли, було → стало).
              </Typography>
            </>}
            {(dlg.err || (pv && pv.error)) && <Typography sx={{ color: '#ff8a80', fontSize: 13, mt: 1 }}>⛔ {dlg.err || pv.error}</Typography>}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={() => setDlg(null)} disabled={dlg.busy} sx={{ color: '#aaa', textTransform: 'none' }}>Скасувати</Button>
            <Button variant="contained" onClick={apply} disabled={dlg.busy || !pv || !!pv.error}
              sx={{ bgcolor: 'var(--ac1, #7c4dff)', textTransform: 'none' }}>
              {dlg.to === 'on' ? '✅ Відкрити цьому юзеру' : dlg.to === 'off' ? '⛔ Сховати цьому юзеру' : '↩ Повернути як усім'}
            </Button>
          </DialogActions>
        </>}
      </Dialog>
    </Paper>
  );
}

export default function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialDays, setTrialDays] = useState('');
  const [pcDays, setPcDays] = useState('');
  // 🎁 2026-09-11: подарунок днів підписки. Такої дії не існувало взагалі —
  // підписку рухала лише оплата.
  const [subDays, setSubDays] = useState('30');
  const [subPkg, setSubPkg] = useState('');
  const [subNotify, setSubNotify] = useState(true);
  const [subMsg, setSubMsg] = useState('');
  const [subBusy, setSubBusy] = useState(false);
  const [pkgList, setPkgList] = useState([]);
  useEffect(() => {
    fetch('/api/packages', { headers: { Authorization: 'Bearer ' + localStorage.getItem('dashboard_token') } })
      .then(r => r.json()).then(d => setPkgList(Array.isArray(d) ? d : (d.packages || []))).catch(() => {});
  }, []);
  const [userMaxMb, setUserMaxMb] = useState('');
  const [userMaxSec, setUserMaxSec] = useState('');
  const [userSoftLimit, setUserSoftLimit] = useState('');
  const [userAdMb, setUserAdMb] = useState('');
  const [userAdSec, setUserAdSec] = useState('');
  const [userCkMb, setUserCkMb] = useState('');
  const [userCkSec, setUserCkSec] = useState('');
  const [userCkCount, setUserCkCount] = useState('');
  const [userOvCount, setUserOvCount] = useState('');
  const [userOvMb, setUserOvMb] = useState('');
  const [userOvSec, setUserOvSec] = useState('');
  const [auxUsage, setAuxUsage] = useState(null);
  const [simRows, setSimRows] = useState(null);     // [D-570] заміри схожості готових версій з оригіналом
  const [vLedger, setVLedger] = useState(null);     // [D-603] журнал версій за обрану добу + пояснення калькулятора
  const [vlToday, setVlToday] = useState(null);     // [D-603] те саме за сьогодні (плитка «Відео сьогодні»)
  const [vlOpen, setVlOpen] = useState(false);
  const [howTab, setHowTab] = useState(null);       // [D-608] «🧮 як пораховано»: subscription_end | trial_end | pcapp_until | money
  const [howAccess, setHowAccess] = useState(null);
  const [howMoney, setHowMoney] = useState(null);
  // [D-324] токени для спецефектів v2 — баланс і нарахування
  const [tokBal, setTokBal] = useState(null);
  const [tokLog, setTokLog] = useState([]);
  const [tokAmt, setTokAmt] = useState('');
  const [tokNote, setTokNote] = useState('');
  const [tokMsg, setTokMsg] = useState('');
  // [D-326] право на платну озвучку власним голосом (дефолт — вимкнено всім)
  const [proDub, setProDub] = useState(null);
  // 💎 Media: reorder карток налаштувань (drag-handle ⠿ + CSS order, persist у localStorage)
  const DEFAULT_CARD_ORDER = ['trial','fileLimits','usage','auxLimits','tokens','ava','uniq','title','screenProt','eruda','message'];
  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('ud_card_order_v1'));
      if (Array.isArray(saved) && saved.length) {
        const merged = saved.filter(x => DEFAULT_CARD_ORDER.includes(x));
        DEFAULT_CARD_ORDER.forEach(x => { if (!merged.includes(x)) merged.push(x); });
        return merged;
      }
    } catch(e) {}
    return DEFAULT_CARD_ORDER;
  });
  const dragCardId = React.useRef(null);
  const onCardDrop = (targetId) => {
    const src = dragCardId.current; dragCardId.current = null;
    if (!src || src === targetId) return;
    setCardOrder(prev => {
      const arr = prev.filter(x => x !== src);
      const ti = arr.indexOf(targetId);
      arr.splice(ti < 0 ? arr.length : ti, 0, src);
      try { localStorage.setItem('ud_card_order_v1', JSON.stringify(arr)); } catch(e) {}
      return arr;
    });
  };
  const resetCardOrder = () => { try { localStorage.removeItem('ud_card_order_v1'); } catch(e) {} setCardOrder([...DEFAULT_CARD_ORDER]); };
  const ord = (id) => { const i = cardOrder.indexOf(id); return i < 0 ? 99 : i; };

  // [D-408] Доступ цього юзера до функцій «Посилення».
  // null — ще не завантажено, [] — довідник порожній або помилка: у
  // обох випадках картка просто не малюється й нічого не ламає.
  const [ufRows, setUfRows] = useState(null);
  const [ufBusy, setUfBusy] = useState('');
  const [ufChange, setUfChange] = useState(null);   // [D-560] { key, state, n } — що щойно змінили: телефон праворуч покаже це одразу
  const [verN, setVerN] = useState(0);   // [D-691] «🧭 Версії» змінили показ - телефон Студії праворуч перечитується
  useEffect(() => {
    if (!id) return;
    api.get(`/users/${id}/features`)
      .then(r => setUfRows((r.data && r.data.rows) || []))
      .catch(() => setUfRows([]));
  }, [id]);


  const setUserFeat = (key, state) => {
    setUfBusy(key);
    api.post(`/users/${id}/features?key=${encodeURIComponent(key)}&state=${state}`)
      .then(r => { setUfRows((r.data && r.data.rows) || []); setUfChange({ key, state, n: Date.now() }); })
      .catch(() => {})
      .finally(() => setUfBusy(''));
  };
  const DragHandle = ({ id }) => (
    <Box draggable onDragStart={()=>{ dragCardId.current = id; }} onDragEnd={()=>{ dragCardId.current = null; }}
      title="Перетягни щоб змінити порядок"
      sx={{ cursor:'grab', color:'#777', fontSize:14, lineHeight:1, textAlign:'center', userSelect:'none',
            letterSpacing:2, py:0.3, mb:0.5, bgcolor:'rgba(var(--ac1rgb, 124, 77, 255),0.10)', borderRadius:0.5,
            '&:active':{ cursor:'grabbing' } }}>⠿ ⠿ ⠿</Box>
  );
  const [userShowUniq, setUserShowUniq] = useState(null);
  const [userScreenProt, setUserScreenProt] = useState(null);
  const [msg, setMsg] = useState('');
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actLoading, setActLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  const [costData, setCostData] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [msgResult, setMsgResult] = useState('');
  const [msgFile, setMsgFile] = useState(null);
  const [msgMediaType, setMsgMediaType] = useState('photo');
  const [msgSending, setMsgSending] = useState(false);
  const [chatKey, setChatKey] = useState(0);
  const [erudaEnabled, setErudaEnabled] = useState(null);
  const [userWds, setUserWds] = useState([]); // [МЕДІА УНІКАЛІЗАТОР] заявки на вивід цього юзера
  const [userReceipts, setUserReceipts] = React.useState([]); // D-246 hookfix: ДО early-return (Rules of Hooks)
  const [receiptsOpen, setReceiptsOpen] = React.useState(false);
  const [avaLim, setAvaLim] = useState(null);
  const [avaEdit, setAvaEdit] = useState(false);
  const [avaForm, setAvaForm] = useState({});
  const [avaMsg, setAvaMsg] = useState('');
  // [D-321] персональний ліміт версій «Посилення» для цього юзера.
  // 0 / порожньо = діє загальний ліміт із налаштувань.
  const [hyperMax, setHyperMax] = useState('');
  const loadTokens = (tgId) => {
    if (!tgId) return;
    api.get('/media/tokens', { params: { tg_id: tgId } })
      .then(r => { setTokBal(Number(r.data?.balance || 0)); setTokLog(r.data?.log || []); })
      .catch(() => setTokBal(0));
  };
  const grantTokens = (tgId, amount, note) => {
    const am = Number(amount);
    if (!tgId || !am) { setTokMsg('❌ Вкажи кількість'); return; }
    api.post('/media/tokens', { tg_id: Number(tgId), amount: am, note: note || '' })
      .then(r => {
        setTokMsg((am > 0 ? '✅ Нараховано ' : '✅ Знято ') + Math.abs(am) + ' ток. · баланс ' + r.data.balance);
        setTokAmt(''); setTokNote('');
        loadTokens(tgId);
      })
      .catch(e => setTokMsg('❌ ' + (e.response?.data?.detail || 'Помилка')));
  };
  const loadHyperMax = (tgId) => {
    if (!tgId) return;
    api.get('/referral/settings')
      .then(r => {
        const d = r.data || {};
        setHyperMax(String(d['uniq_hyper_max_u' + tgId] || ''));
        const per = d['uniq_dub_pro_u' + tgId];
        // порожньо → діє загальний дефолт (він теж вимкнений)
        setProDub(per === undefined || per === null || per === ''
          ? String(d.uniq_dub_pro_default || '0') === '1'
          : String(per) === '1');
      })
      .catch(() => {});
  };
  const saveProDub = (tgId, on) => {
    if (!tgId) return;
    setProDub(on);
    api.post('/referral/settings', { ['uniq_dub_pro_u' + tgId]: on ? '1' : '0' })
      .then(() => setTokMsg(on ? '✅ Якісну озвучку УВІМКНЕНО цьому юзеру' : '⛔ Якісну озвучку вимкнено'))
      .catch(() => setTokMsg('❌ Не вдалося зберегти'));
  };
  const saveHyperMax = (tgId, v) => {
    const n = Math.max(0, Math.min(30, Number(v) || 0));
    setHyperMax(String(n || ''));
    api.post('/referral/settings', { ['uniq_hyper_max_u' + tgId]: String(n) }).catch(() => {});
  };
  const loadAvaLim = (tg_id) => {
    if (!tg_id) return;
    const tok = localStorage.getItem('dashboard_token');
    fetch('/api/miniapp/avatar/user-limits', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { const found = (d.users || []).find(x => String(x.tg_id) === String(tg_id)); setAvaLim(found || null); } })
      .catch(() => {});
  };

  // [МЕДІА УНІКАЛІЗАТОР] заявки на вивід цього юзера (з усіх — фільтр по user_id)
  const reloadUserWds = () => fetch('/api/withdrawals', { headers:{ Authorization:'Bearer '+localStorage.getItem('dashboard_token') } })
    .then(r => r.ok ? r.json() : []).then(d => setUserWds(Array.isArray(d) ? d.filter(w => String(w.user_id) === String(id)) : [])).catch(()=>{});
  const userWdPaid = async (w) => {
    if (!window.confirm('Підтвердити: ти ВЖЕ переказав '+w.amount+'€ ('+w.method+')? Баланс буде списано.')) return;
    const tok = localStorage.getItem('dashboard_token');
    const r = await fetch('/api/withdrawals/'+w.id+'/paid', { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    const j = await r.json().catch(()=>({})); if (!r.ok) { alert((j&&j.detail)||'Помилка'); return; }
    reloadUserWds(); getUser(id).then(r => setData(r.data));
  };
  const userWdReject = async (w) => {
    const note = window.prompt('Причина відхилення (юзеру видно):',''); if (note===null) return;
    const tok = localStorage.getItem('dashboard_token');
    await fetch('/api/withdrawals/'+w.id+'/reject?note='+encodeURIComponent(note), { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    reloadUserWds();
  };

  useEffect(() => {
    reloadUserWds();
    getUser(id).then(r => { setData(r.data); setLoading(false); loadAvaLim(r.data?.user?.telegram_id); loadHyperMax(r.data?.user?.telegram_id); loadTokens(r.data?.user?.telegram_id); setUserMaxMb(String(r.data?.user?.max_file_size_mb||'')); setUserMaxSec(String(r.data?.user?.max_duration_sec||'')); setUserSoftLimit(String(Math.round((r.data?.user?.soft_time_limit||0)/60)||'')); setUserAdMb(String(r.data?.user?.max_ad_size_mb||'')); setUserAdSec(String(r.data?.user?.max_ad_duration_sec||'')); setUserCkMb(String(r.data?.user?.max_ck_size_mb||'')); setUserCkSec(String(r.data?.user?.max_ck_duration_sec||'')); setUserCkCount(String(r.data?.user?.max_ck_count||'')); setUserOvCount(String(r.data?.user?.max_overlay_count||'')); setUserOvMb(String(r.data?.user?.max_overlay_size_mb||'')); setUserOvSec(String(r.data?.user?.max_overlay_duration_sec||'')); setUserShowUniq(r.data?.user?.show_uniq_info); setUserScreenProt(r.data?.user?.screen_protection !== false); });
    fetch(`/api/users/${id}/aux-usage`, { headers:{ Authorization:'Bearer '+localStorage.getItem('dashboard_token') } })
      .then(r => r.ok ? r.json() : null).then(d => setAuxUsage(d)).catch(()=>setAuxUsage(null));
    fetch(`/api/users/${id}/similarity`, { headers:{ Authorization:'Bearer '+localStorage.getItem('dashboard_token') } })
      .then(r => r.ok ? r.json() : null).then(d => setSimRows((d && d.rows) || [])).catch(()=>setSimRows([]));
    getUserActivity(id).then(r => setDates(r.data));
    loadVLedger(null, setVlToday);
    getUserCostsByDate(id).then(r => setCostData(r.data));
    const tok = localStorage.getItem('dashboard_token');
    fetch(`/api/users/${id}/eruda`, { headers:{ Authorization:'Bearer '+tok } })
      .then(r => r.ok ? r.json() : { enabled:false }).then(d => setErudaEnabled(!!d.enabled)).catch(()=>setErudaEnabled(false));
  }, [id]);

  const deleteSession = async (sessionStartTime, origin) => {
    if (!window.confirm(origin === 'staging' ? 'Видалити цю 🧪 STAGING відео-сесію?' : 'Видалити цю відео-сесію?')) return;
    try {
      // D-204: origin-роутинг — staging видаляється у staging_postgres через бекенд, prod — у prod
      await api.delete(`/users/${id}/activity/session`, { params: { session_start: sessionStartTime, origin: origin || 'prod' } });
      // Перезавантажуємо активність
      getUserActivity(id, selectedDate).then(r => setActivities(r.data));
      getUserActivity(id).then(r => setDates(r.data));
    } catch(e) { alert('Помилка видалення: ' + e.message); }
  };

  const sendMsg = async () => {
    if (!msgText.trim() && !msgFile) return;
    setMsgSending(true);
    try {
      const tok = localStorage.getItem('dashboard_token');
      if (msgFile) {
        const fd = new FormData();
        fd.append('message', msgText);
        fd.append('file', msgFile);
        fd.append('media_type', msgMediaType);
        const r = await fetch('/api/users/'+id+'/message?message='+encodeURIComponent(msgText)+'&media_type='+msgMediaType,
          { method:'POST', headers:{Authorization:'Bearer '+tok}, body:fd });
        const d = await r.json();
        setMsgResult(d.status==='ok' ? '✅ Надіслано!' : '❌ Помилка');
      } else {
        await sendMessageToUser(id, msgText);
        setMsgResult('✅ Надіслано!');
      }
      setMsgText(''); setMsgFile(null);
      setChatKey(k => k+1);
      setTimeout(() => setMsgResult(''), 3000);
    } catch { setMsgResult('❌ Помилка'); }
    setMsgSending(false);
  };

  // [D-603] журнал версій + пояснення калькулятора (лише власник; без доступу - нічого не показуємо)
  const loadVLedger = (day, setter) => fetch('/api/users/' + id + '/versions-ledger' + (day ? '?day=' + day : ''), { headers: { Authorization: 'Bearer ' + localStorage.getItem('dashboard_token') } })
    .then(r => r.ok ? r.json() : null).then(d => setter(d)).catch(() => setter(null));

  // [D-608] журнали строків і грошей + пояснення калькулятора (лише власник)
  const openHow = (tab) => {
    setHowTab(tab);
    const h = { headers: { Authorization: 'Bearer ' + localStorage.getItem('dashboard_token') } };
    fetch('/api/users/' + id + '/access-ledger', h).then(r => r.ok ? r.json() : null).then(setHowAccess).catch(() => setHowAccess(null));
    fetch('/api/users/' + id + '/money-ledger', h).then(r => r.ok ? r.json() : null).then(setHowMoney).catch(() => setHowMoney(null));
  };

  const loadActivities = (date) => {
    setSelectedDate(date);
    setActLoading(true);
    loadVLedger(date, setVLedger);
    getUserActivity(id, date).then(r => { setActivities(r.data); setActLoading(false); });
  };

  const handleDeleteDate = async (date, origin) => {
    if (!window.confirm('Видалити активність за ' + date + (origin === 'staging' ? ' (🧪 STAGING)' : '') + '?')) return;
    await api.delete(`/users/${id}/activity`, { params: { date, origin: origin || 'prod' } });
    setDates(dates.filter(d => d.date !== date));
    if (selectedDate === date) { setSelectedDate(null); setActivities([]); }
  };

  const handleScreenProt = async (val) => {
    try {
      const tok = localStorage.getItem('dashboard_token');
      await fetch(`/api/users/${id}/screen-protection?enabled=${val}`,
        { method:'POST', headers:{ Authorization:'Bearer '+tok } });
      setUserScreenProt(val);
      setMsg('✅ Збережено!');
      setTimeout(()=>setMsg(''),3000);
    } catch(e) { setMsg('❌ Помилка'); }
  };
  const handleEruda = async (val) => {
    try {
      const tok = localStorage.getItem('dashboard_token');
      await fetch(`/api/users/${id}/eruda?enabled=${val}`,
        { method:'POST', headers:{ Authorization:'Bearer '+tok } });
      setErudaEnabled(val);
      setMsg('✅ Збережено!');
      setTimeout(()=>setMsg(''),3000);
    } catch(e) { setMsg('❌ Помилка'); }
  };
  const handleShowUniq = async (val) => {
    try {
      const tok = localStorage.getItem('dashboard_token');
      await fetch(`/api/users/${id}/show-uniq-info?enabled=${val}`,
        { method:'POST', headers:{ Authorization:'Bearer '+tok } });
      setUserShowUniq(val);
      setMsg('✅ Збережено!');
      setTimeout(()=>setMsg(''),3000);
    } catch(e) { setMsg('❌ Помилка'); }
  };
  // [D-750] яке найдовше відео зараз діє для юзера і чиє (власник юзеру / партнер учням / загальне) - рахує сервер (calc.upload_max_sec)
  const [maxVid, setMaxVid] = useState(null);
  const loadMaxVid = useCallback(() => {
    const tok = localStorage.getItem('dashboard_token');
    fetch(`/api/users/${id}/max-video`, { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => r.ok ? r.json() : null).then(setMaxVid).catch(() => setMaxVid(null));
  }, [id]);
  useEffect(() => { loadMaxVid(); }, [loadMaxVid]);
  const handleFileLimits = async () => {
    try {
      const tok = localStorage.getItem('dashboard_token');
      await fetch(`/api/users/${id}/file-limits?max_file_size_mb=${userMaxMb||0}&max_duration_sec=${userMaxSec||0}&soft_time_limit_min=${userSoftLimit||0}`,
        { method:'POST', headers:{ Authorization:'Bearer '+tok } });
      loadMaxVid();
      setMsg('✅ Ліміти збережено!');
      setTimeout(()=>setMsg(''),3000);
    } catch(e) { setMsg('❌ Помилка'); }
  };
  const handleAuxLimits = async () => {
    try {
      const tok = localStorage.getItem('dashboard_token');
      await fetch(`/api/users/${id}/aux-limits?max_ad_size_mb=${userAdMb||0}&max_ad_duration_sec=${userAdSec||0}&max_ck_size_mb=${userCkMb||0}&max_ck_duration_sec=${userCkSec||0}&max_ck_count=${userCkCount||0}&max_overlay_count=${userOvCount||0}&max_overlay_size_mb=${userOvMb||0}&max_overlay_duration_sec=${userOvSec||0}`,
        { method:'POST', headers:{ Authorization:'Bearer '+tok } });
      setMsg('✅ Ліміти збережено!');
      setTimeout(()=>setMsg(''),3000);
    } catch(e) { setMsg('❌ Помилка'); }
  };
  const handleTrial = async () => {
    try {
      await updateTrialUser(id, parseInt(trialDays));
      setMsg('Trial оновлено!');
      setUserMaxMb(String(data?.max_file_size_mb||''));
      setUserMaxSec(String(data?.max_duration_sec||''));
      getUser(id).then(r => setData(r.data));
    } catch { setMsg('Помилка'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }}/></Box>;
  if (!data) return <Typography sx={{ color: '#f44' }}>Не знайдено</Typography>;
  const u = data.user;
  const sessions = groupSessions(activities);

  return (
    <Box>
      <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center', mb:2}}>
        <Button onClick={() => navigate('/users')} sx={{ color: 'var(--ac1, #7c4dff)' }}>← Назад</Button>
        <Button color="error" variant="outlined" startIcon={<DeleteIcon/>} onClick={async () => {
          if (!window.confirm(`УВАГА! Це видалить @${u.telegram_username} зі ВСІХ таблиць БД (юзери, реферали, платежі, чат, історія тріалів, тощо). Дія НЕЗВОРОТНА!\n\nПродовжити?`)) return;
          if (!window.confirm('Точно видалити? Останнє попередження!')) return;
          try {
            const res = await api.delete(`/users/${u.id}/full_delete`);
            alert(`Видалено @${res.data.username}\n\n${JSON.stringify(res.data.deleted, null, 2)}`);
            navigate('/users');
          } catch(e) { alert('Помилка: ' + (e.response?.data?.detail || e.message)); }
        }}>🗑 Видалити юзера повністю</Button>
      </Box>
      <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 'bold' }}>👤 @{u.telegram_username || '?'}</Typography>
      <UserTestFlag userId={id} />
      {/* [D-768] OWNER 30.10: «не бачу у списку юзерів ... входи у застосунки».
          Мовчить, коли переходів немає. */}
      <UserAppClicks userId={id} />
      {/* [D-771] OWNER 30.10 «дані йому особисто - Так»: теми з «👤 Юзеру…» - тут, із «✕ Забрати»; мовчить, коли їх немає */}
      <UserThemesGiven userId={id} onChanged={() => setVerN(x => x + 1)} />
      <VersionsBlock userId={id} onChanged={() => { setVerN(x => x + 1); api.get(`/users/${id}/features`).then(r => setUfRows((r.data && r.data.rows) || [])).catch(() => {}); }} />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[{l:'Статус',v:u.status},{l:'Trial до',v:u.trial_end?u.trial_end.substring(0,10):'—'},{l:'Ліміт/день',v:u.unlimited?'∞':u.daily_limit},{l:'Відео сьогодні',v:u.videos_today||0,vl:true},{l:'Всього відео',v:(u.total_videos||0)+' ('+( u.total_versions||0)+' верс.)'}].concat(
          // [D-731] обробки на пристрої юзера - окремо від серверних (версії - ті, про які застосунок повідомив)
          Object.keys(u.device_totals || {}).length ? [{ l:'📱 Телефон · 💻 ПК', v: ['phone','pc'].filter(k => u.device_totals[k]).map(k =>
            (k === 'phone' ? '📱 ' : '💻 ') + u.device_totals[k].jobs + ' (' + u.device_totals[k].versions + ' верс.)').join(' · ') }] : []
        ).map(item => (
          <Grid item xs={6} md={2} key={item.l}>
            <Card sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', cursor: item.vl ? 'pointer' : 'default' }} onClick={item.vl ? () => { loadVLedger(null, setVlToday); setVlOpen(true); } : undefined}>
              <CardContent sx={{ p: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#aaa' }}>{item.l}</Typography>
                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 'bold' }}>{item.v}</Typography>
                {item.vl && <Typography variant="caption" sx={{ color: '#9a86ff', display: 'block' }}>🧮 як пораховано</Typography>}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {/* Квитанції card */}
        <Grid item xs={6} md={2}>
          <Card sx={{ bgcolor: '#1a1a2e', border: '1px solid var(--ac1, #7c4dff)', cursor: 'pointer', '&:hover': { border: '1px solid var(--ac2, #a78bfa)' } }}
            onClick={async () => {
              try {
                const r = await api.get('/receipts?user_id=' + id);
                setUserReceipts(r.data || []);
              } catch { setUserReceipts([]); }
              setReceiptsOpen(true);
            }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#aaa' }}>🧾 Квитанції</Typography>
              <Typography variant="body1" sx={{ color: 'var(--ac1, #7c4dff)', fontWeight: 'bold' }}>→</Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* [МЕДІА УНІКАЛІЗАТОР] активна заявка на вивід — біля «Всього відео» */}
        {userWds.filter(w => w.status === 'pending').map(w => (
          <Grid item xs={12} md={4} key={'wd'+w.id}>
            <Card sx={{ bgcolor: '#1a1a2e', border: '1px solid #ff9800' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#ffb74d', fontWeight:'bold' }}>💸 Заявка на вивід #{w.id}</Typography>
                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 'bold' }}>€{w.amount} · {w.method} {w.src === 'media' ? '🎬' : '💎'}</Typography>
                {w.currency && w.currency !== 'EUR' && w.cur_amount && (
                  <Typography variant="caption" sx={{ color:'#ffb74d', display:'block' }}>→ переказати {w.cur_amount} {w.currency}{w.rate ? ' (курс ' + w.rate + ')' : ''}</Typography>
                )}
                <Typography variant="caption" sx={{ color:'#999', display:'block', mb:1, wordBreak:'break-word' }}>{w.details}</Typography>
                {w.comment && <Typography variant="caption" sx={{ color:'#90caf9', display:'block', mb:1 }}>💬 {w.comment}</Typography>}
                <Box sx={{ display:'flex', gap:0.5 }}>
                  <Button size="small" variant="outlined" onClick={()=>userWdPaid(w)} sx={{ borderColor:'#4caf50', color:'#4caf50', fontSize:11, minWidth:0 }}>✅ Виплачено</Button>
                  <Button size="small" onClick={()=>userWdReject(w)} sx={{ color:'#f44', fontSize:11, minWidth:0 }}>✖ Відхилити</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* [D-560] OWNER 13.10: «зліва список функцій… а справа все вільно… хотів щоб справа було зафіксоване прев'ю телефона який так само
          буде показувати у реальному часі всі зміни… як у (Дозволи і теми)». Три стани, не два: «Як усім» — це ВІДСУТНІСТЬ запису. */}
      {ufRows && ufRows.length > 0 && (
      <Paper id="ud-feats" sx={{ bgcolor: '#16202c', border: '1px solid rgba(0,229,160,.3)', p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1.2 }}>
          <Typography variant="subtitle1" sx={{ color: '#7ffcd3' }}>
            🎛 Функції юзера — і телефон, яким він їх бачить
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: '#8fa3b8', flex: 1, minWidth: 260 }}>
            «Як усім» — юзер іде за загальним налаштуванням. «Показувати» — кнопка точно є. «Сховати» — кнопки немає і функція не працює.
          </Typography>
          <Button size="small" onClick={() => { const e = document.getElementById('ud-cards'); e && e.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
            sx={{ color: '#7ffcd3', fontSize: 12, textTransform: 'none' }}>↓ до карток: тріал, ліміти, токени</Button>
        </Box>
        <UserStudioPhone userId={id} rows={ufRows} onSet={setUserFeat} busyKey={ufBusy} change={ufChange} ver={verN} />
      </Paper>
      )}

      {/* 💎 Media: reorder карток — flex + CSS order; drag-handle ⠿ на кожній; порядок у localStorage */}
      <Box id="ud-cards" sx={{ display:'flex', justifyContent:'flex-end', mb:1 }}>
        <Button size="small" onClick={resetCardOrder} sx={{ color:'#888', fontSize:12 }}>↺ Скинути порядок карток</Button>
      </Box>
      <Box sx={{ display:'flex', flexWrap:'wrap', alignItems:'flex-start', gap:2, mb:3,
        '& > .MuiPaper-root': { maxWidth:'none !important', mb:0, width:{ xs:'100%', sm:'calc(50% - 8px)', lg:'calc(33.333% - 11px)' } } }}>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('trial')} sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2, mb: 3, maxWidth: 400, order: ord('trial') }}>
        <DragHandle id="trial"/>
        <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 1 }}>⏳ Trial</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField type="number" label="Днів" size="small" value={trialDays} onChange={e => setTrialDays(e.target.value)}
            sx={{ width: 100, input:{color:'#fff'}, label:{color:'#aaa'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          <Button variant="contained" onClick={handleTrial} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>Оновити</Button>
          {msg && <Typography sx={{ color: '#4caf50', fontSize: 13 }}>{msg}</Typography>}
        </Box>
      </Paper>

      {/* 🎁 2026-09-11: подарунок днів підписки. Пакет лишаємо порожнім — тоді
          ліміти юзера не чіпаються і рухається лише дата; вибір пакета потрібен
          тим, у кого пакета ще немає (інакше «доступ» був би без ліміту відео). */}
      <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid var(--ac1, #7c4dff)', p: 2, mb: 3, maxWidth: 400 }}>
        <Typography variant="subtitle1" sx={{ color: '#b39ddb', mb: 1 }}>🎁 Подарувати дні підписки</Typography>
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
          Зараз: {u.subscription_end ? '✅ до ' + String(u.subscription_end).substring(0, 10) : '⛔ без підписки'}
          {u.unlimited ? ' · безліміт' : (u.daily_limit ? ' · ' + u.daily_limit + ' відео/день' : '')}
          {' '}<Chip size="small" label="🧮 як пораховано" clickable onClick={() => openHow('subscription_end')} sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(154,134,255,.15)', color: '#b39ddb' }} />
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {[7, 14, 30, 90].map(n => (
            <Chip key={n} label={'+' + n} size="small" clickable onClick={() => setSubDays(String(n))}
              sx={{ bgcolor: String(n) === String(subDays) ? 'rgba(var(--ac1rgb, 124, 77, 255),.45)' : 'rgba(255,255,255,.07)',
                    color: String(n) === String(subDays) ? '#fff' : '#aaa' }} />
          ))}
          <TextField size="small" type="number" value={subDays} onChange={e => setSubDays(e.target.value)}
            sx={{ width: 80, input: { color: '#fff' } }} />
          <Button size="small" variant="contained" disabled={subBusy}
            sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}
            onClick={async () => {
              const n = parseInt(subDays, 10);
              if (!n || n < 1) { setSubMsg('❌ Вкажи кількість днів'); return; }
              setSubBusy(true); setSubMsg('…');
              const q = '/api/users/' + id + '/subscription-grant?days=' + n
                + (subPkg ? '&package_id=' + subPkg : '') + '&notify=' + (subNotify ? 'true' : 'false');
              const r = await fetch(q, { method: 'POST', headers: { Authorization: 'Bearer ' + localStorage.getItem('dashboard_token') } });
              const j = await r.json().catch(() => ({}));
              if (!r.ok) setSubMsg('❌ ' + (j.detail || 'Помилка'));
              else {
                let m = '✅ +' + n + ' дн. · до ' + String(j.subscription_end || '').substring(0, 10);
                if (!subNotify) m += ' · без вітання';
                else if (j.notified) m += ' · вітання надіслано' + (j.notify_how === 'photo' ? ' 🖼' : ' (текстом)') + (j.notify_effect ? ' 🎉' : '');
                else m += ' · ⚠️ вітання НЕ дійшло: ' + (j.notify_error || 'невідома причина');
                setSubMsg(m);
                getUser(id).then(rr => setData(rr.data));
              }
              setSubBusy(false);
            }}>{subBusy ? '…' : '🎁 Подарувати'}</Button>
        </Box>
        <TextField select size="small" fullWidth value={subPkg} onChange={e => setSubPkg(e.target.value)}
          SelectProps={{ native: true }} sx={{ mt: 1, '& select': { color: '#fff' } }}>
          <option value="">пакет не міняти (лишити як є)</option>
          {pkgList.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}{p.daily_limit ? ' · ' + p.daily_limit + '/день' : ' · безліміт'}
            </option>
          ))}
        </TextField>
        <FormControlLabel sx={{ mt: .3, ml: 0 }}
          control={<Switch size="small" checked={subNotify} onChange={e => setSubNotify(e.target.checked)} />}
          label={<Typography sx={{ color: '#888', fontSize: 12 }}>🎉 святкове вітання у бот</Typography>} />
        {subMsg && <Typography sx={{ color: subMsg.startsWith('✅') ? '#4caf50' : (subMsg === '…' ? '#888' : '#f44336'), fontSize: 13 }}>{subMsg}</Typography>}
      </Paper>

      <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #00bcd4', p: 2, mb: 3, maxWidth: 400 }}>
        <Typography variant="subtitle1" sx={{ color: '#00bcd4', mb: 1 }}>🖥 PC Unlimited (Autopilot Studio)</Typography>
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
          Доступ: {u.pcapp_until && new Date(u.pcapp_until) > new Date() ? '✅ активний до ' + String(u.pcapp_until).substring(0, 10) : '⛔ немає'}
          {' '}<Chip size="small" label="🧮 як пораховано" clickable onClick={() => openHow('pcapp_until')} sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(0,188,212,.15)', color: '#00bcd4' }} />
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button size="small" variant="contained" sx={{ bgcolor: '#00bcd4' }} onClick={async () => {
            const r = await fetch('/api/users/' + id + '/pcapp-grant', { method: 'POST', headers: { Authorization: 'Bearer ' + localStorage.getItem('dashboard_token') } });
            const j = await r.json().catch(() => ({}));
            setMsg(r.ok ? '✅ PC-доступ видано до ' + String(j.pcapp_until || '').substring(0, 10) : '❌ ' + (j.detail || 'Помилка'));
            if (r.ok) getUser(id).then(rr => setData(rr.data));
          }}>➕ Видати (період за замовч.)</Button>
          <TextField size="small" type="number" placeholder="днів" value={pcDays} onChange={e => setPcDays(e.target.value)} sx={{ width: 90, input: { color: '#fff' } }} />
          <Button size="small" variant="outlined" sx={{ borderColor: '#00bcd4', color: '#00bcd4' }} onClick={async () => {
            const r = await fetch('/api/users/' + id + '/pcapp-grant?days=' + (pcDays || 0), { method: 'POST', headers: { Authorization: 'Bearer ' + localStorage.getItem('dashboard_token') } });
            const j = await r.json().catch(() => ({}));
            setMsg(r.ok ? '✅ PC-доступ до ' + String(j.pcapp_until || '').substring(0, 10) : '❌ ' + (j.detail || 'Помилка'));
            if (r.ok) getUser(id).then(rr => setData(rr.data));
          }}>➕ N днів</Button>
          <Button size="small" variant="outlined" color="error" onClick={async () => {
            const r = await fetch('/api/users/' + id + '/pcapp-grant?revoke=true', { method: 'POST', headers: { Authorization: 'Bearer ' + localStorage.getItem('dashboard_token') } });
            setMsg(r.ok ? '✅ PC-доступ забрано' : '❌ Помилка');
            if (r.ok) getUser(id).then(rr => setData(rr.data));
          }}>🚫 Забрати</Button>
        </Box>
      </Paper>

      {/* [D-608] «🧮 як пораховано»: строки доступу і гроші - з журналів (D-607), рахує калькулятор */}
      <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #2a3a4e', p: 2, mb: 3, maxWidth: 400 }}>
        <Typography variant="subtitle1" sx={{ color: '#9a86ff', mb: 1 }}>🧮 Як пораховано</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[['subscription_end', '📅 Підписка'], ['trial_end', '🎟 Тріал'], ['pcapp_until', '🖥 ПК'], ['money', '💶 Гроші']].map(([k, l]) =>
            <Chip key={k} label={l} clickable onClick={() => openHow(k)} sx={{ bgcolor: 'rgba(255,255,255,.07)', color: '#ddd' }} />)}
        </Box>
      </Paper>

      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('fileLimits')} sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2, mb: 3, maxWidth: 400, order: ord('fileLimits') }}>
        <DragHandle id="fileLimits"/>
        <Typography variant="subtitle1" sx={{ color: '#ff9800', mb: 1 }}>📦 Ліміти файлів Основного відео</Typography>
        <Typography variant="caption" sx={{ color:'#888', display:'block', mb:1 }}>0 = глобальний ліміт</Typography>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'flex-end' }}>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>📦 МБ</Typography>
            <TextField type="number" size="small" value={userMaxMb} onChange={e=>setUserMaxMb(e.target.value)}
              inputProps={{ min:0, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>⏱ Найдовше відео, сек</Typography>
            <TextField type="number" size="small" value={userMaxSec} onChange={e=>setUserMaxSec(e.target.value)}
              inputProps={{ min:0, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>⏳ Обробка (хв)</Typography>
            <TextField type="number" size="small" value={userSoftLimit} onChange={e=>setUserSoftLimit(e.target.value)}
              inputProps={{ min:0, max:300, style:{color:'#fff', width:80} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}
              title="Per-user soft_time_limit. 0 = глобальний (120 хв). VIP юзери з відео 1-2 год: збільшити (max 300=5 год)."/>
          </Box>
          <Button variant="contained" onClick={handleFileLimits} sx={{ bgcolor:'#ff9800' }}>Зберегти</Button>
        </Box>
        <BgvidLimit userId={id} />{/* [D-804] */}
        <DetextFillLimit userId={id} />{/* [D-816] */}
        {maxVid && (
          <Typography variant="caption" sx={{ color: '#ffcc80', display: 'block', mt: 1 }}>
            ⏱ Зараз бот, Студія й застосунки приймають відео до <b>{maxVid.text}</b>
            {maxVid.src === 'user' ? ' - ваш ліміт цьому юзеру' : maxVid.src === 'partner' ? ` - ліміт партнера «${maxVid.partner || ''}» для учнів` : ' - загальний (Налаштування)'}
          </Typography>
        )}
      </Paper>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('usage')} sx={{ bgcolor: '#1a1a2e', border: '1px solid #2196f3', p: 2, mb: 3, maxWidth: 400, order: ord('usage') }}>
        <DragHandle id="usage"/>
        <Typography variant="subtitle1" sx={{ color: '#2196f3', mb: 1 }}>📊 Споживання (поточне збережене)</Typography>
        {!auxUsage ? (
          <Typography variant="caption" sx={{ color:'#888' }}>Завантаження…</Typography>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ color:'#ff9800', mb:0.5 }}>📢 Реклама</Typography>
            {auxUsage.ad && auxUsage.ad.present ? (
              <Typography variant="body2" sx={{ color:'#ddd', mb:1 }}>
                📦 {auxUsage.ad.size_mb} МБ{auxUsage.ad.duration_sec!=null ? `  ⏱ ${auxUsage.ad.duration_sec} сек` : ''}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color:'#888', mb:1 }}>— немає збереженої реклами</Typography>
            )}
            <Typography variant="body2" sx={{ color:'#4caf50', mb:0.5 }}>🟩 Хромакеї</Typography>
            <Typography variant="body2" sx={{ color:'#ddd' }}>
              🔢 {auxUsage.chromakeys ? auxUsage.chromakeys.count : 0} шт  📦 {auxUsage.chromakeys ? auxUsage.chromakeys.total_size_mb : 0} МБ
            </Typography>
          </Box>
        )}
      </Paper>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('auxLimits')} sx={{ bgcolor: '#1a1a2e', border: '1px solid #ff9800', p: 2, mb: 3, maxWidth: 400, order: ord('auxLimits') }}>
        <DragHandle id="auxLimits"/>
        <Typography variant="subtitle1" sx={{ color: '#ff9800', mb: 1 }}>📦 Ліміти Реклами та Хромакеїв</Typography>
        <Typography variant="caption" sx={{ color:'#888', display:'block', mb:1 }}>0 = глобальний ліміт</Typography>
        <Typography variant="caption" sx={{ color:'#ff9800', display:'block', mb:0.5 }}>📢 Реклама</Typography>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'flex-end', mb:1.5 }}>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>📦 МБ</Typography>
            <TextField type="number" size="small" value={userAdMb} onChange={e=>setUserAdMb(e.target.value)}
              inputProps={{ min:0, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>⏱ Сек</Typography>
            <TextField type="number" size="small" value={userAdSec} onChange={e=>setUserAdSec(e.target.value)}
              inputProps={{ min:0, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
        </Box>
        <Typography variant="caption" sx={{ color:'#4caf50', display:'block', mb:0.5 }}>🟩 Хромакеї</Typography>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'flex-end', mb:1.5 }}>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>📦 МБ</Typography>
            <TextField type="number" size="small" value={userCkMb} onChange={e=>setUserCkMb(e.target.value)}
              inputProps={{ min:0, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>⏱ Сек</Typography>
            <TextField type="number" size="small" value={userCkSec} onChange={e=>setUserCkSec(e.target.value)}
              inputProps={{ min:0, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>🔢 Кількість</Typography>
            <TextField type="number" size="small" value={userCkCount} onChange={e=>setUserCkCount(e.target.value)}
              inputProps={{ min:0, max:20, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
        </Box>
        <Typography variant="caption" sx={{ color:'#2196f3', display:'block', mb:0.5 }}>🖼 Оверлеї</Typography>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'flex-end' }}>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>📦 МБ</Typography>
            <TextField type="number" size="small" value={userOvMb} onChange={e=>setUserOvMb(e.target.value)}
              inputProps={{ min:0, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>⏱ Сек</Typography>
            <TextField type="number" size="small" value={userOvSec} onChange={e=>setUserOvSec(e.target.value)}
              inputProps={{ min:0, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color:'#aaa' }}>🔢 Кількість</Typography>
            <TextField type="number" size="small" value={userOvCount} onChange={e=>setUserOvCount(e.target.value)}
              inputProps={{ min:0, max:20, style:{color:'#fff', width:70} }}
              sx={{ display:'block', mt:0.5, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          </Box>
          <Button variant="contained" onClick={handleAuxLimits} sx={{ bgcolor:'#ff9800' }}>Зберегти</Button>
        </Box>
      </Paper>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('uniq')} sx={{ bgcolor: '#1a1a2e', border: '1px solid #4caf50', p: 2, mb: 3, maxWidth: 400, order: ord('uniq') }}>
        <DragHandle id="uniq"/>
        <Typography variant="subtitle1" sx={{ color: '#4caf50', mb: 1 }}>🔧 Блок унікалізації</Typography>
        <Typography variant="caption" sx={{ color:'#888', display:'block', mb:1 }}>Глобальний за замовчуванням</Typography>
        <Box sx={{ display:'flex', gap:1, alignItems:'center', flexWrap:'wrap' }}>
          <Button size="small" variant={userShowUniq===null?"contained":"outlined"} onClick={()=>handleShowUniq(null)}
            sx={{ bgcolor: userShowUniq===null?'var(--ac1, #7c4dff)':'transparent', borderColor:'var(--ac1, #7c4dff)' }}>Глобальний</Button>
          <Button size="small" variant={userShowUniq===true?"contained":"outlined"} onClick={()=>handleShowUniq(true)}
            sx={{ bgcolor: userShowUniq===true?'#4caf50':'transparent', borderColor:'#4caf50' }}>👁 Показати</Button>
          <Button size="small" variant={userShowUniq===false?"contained":"outlined"} onClick={()=>handleShowUniq(false)}
            sx={{ bgcolor: userShowUniq===false?'#f44336':'transparent', borderColor:'#f44336' }}>🙈 Приховати</Button>
        </Box>
      </Paper>
      {/* [D-321] Персональний ліміт версій «Посилення» */}
      <Paper sx={{ bgcolor: '#16202c', border: '1px solid rgba(0,229,160,.3)', p: 2, mb: 3, maxWidth: 500 }}>
        <Typography variant="subtitle1" sx={{ color: '#7ffcd3', mb: 1 }}>
          🧪 Посилення — ліміт версій
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField
            type="number" size="small" value={hyperMax}
            onChange={e => setHyperMax(e.target.value)}
            onBlur={e => saveHyperMax(data?.user?.telegram_id, e.target.value)}
            inputProps={{ min: 0, max: 30, style: { color: '#eaf6ff', fontWeight: 700 } }}
            sx={{ width: 110,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,229,160,.35)' } }}
            placeholder="—"
          />
          <Typography sx={{ fontSize: 12.5, color: '#a8bece' }}>
            {Number(hyperMax) > 0
              ? `цей юзер може тягнути до ${hyperMax}`
              : 'діє загальний ліміт'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: '#8fa3b8', mt: 1 }}>
          0 або порожньо — беремо загальний ліміт із «🧪 Посилення»
        </Typography>
      </Paper>

      {/* [D-408] Доступ до кожної функції «Посилення» — поіменно.

          Три стани, не два. «Як усім» — це ВІДСУТНІСТЬ запису, і саме
          тому юзер далі йде за загальним налаштуванням: варто OWNER
          змінити дефолт у дашборді, і зміна його дістане. Якби
          «відкрито» писало конкретне значення, юзер назавжди відірвався
          б від загального — і ніхто б не помітив, доки не спитав. */}
      {/* [D-560] картку функцій винесено із сітки: тепер це блок на всю ширину одразу над картками (функції + телефон) */}

      {/* Title customization settings + last-applied jitter audit (Feat 21.05 evening) */}
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('title')} sx={{ bgcolor: '#1a1a2e', border: '1px solid var(--ac1, #7c4dff)', p: 2, mb: 3, maxWidth: 500, order: ord('title') }}>
        <DragHandle id="title"/>
        <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 1 }}>📝 Заставка (Title overlay)</Typography>
        {data?.user?.title_settings ? (() => {
          const ts = data.user.title_settings;
          const la = ts.last_applied || {};
          const enabled = Number(ts.enabled) === 1;
          const bgBase = [Number(ts.bg_r), Number(ts.bg_g), Number(ts.bg_b)];
          const textBase = [Number(ts.text_r), Number(ts.text_g), Number(ts.text_b)];
          const rgb = (a) => `rgb(${a[0]},${a[1]},${a[2]})`;
          const Swatch = ({ rgbArr, label }) => (
            <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5, mr:1.5, mt:0.5 }}>
              <Box sx={{ width:18, height:18, bgcolor:rgb(rgbArr), border:'1px solid #555', borderRadius:0.5 }}/>
              <Typography variant="caption" sx={{ color:'#aaa' }}>{label} {rgb(rgbArr)}</Typography>
            </Box>
          );
          return (
            <>
              <Typography variant="body2" sx={{ color: enabled ? '#4caf50' : '#f44336', mb:0.5 }}>
                {enabled ? '✅ Увімкнено' : '🚫 Вимкнено юзером'}
              </Typography>
              <Box sx={{ mt:1 }}>
                <Typography variant="caption" sx={{ color:'#888', display:'block' }}>Базові кольори (вибрані юзером):</Typography>
                <Swatch rgbArr={bgBase} label="🎨 Фон" />
                <Swatch rgbArr={textBase} label="✏️ Текст" />
              </Box>
              {la.bg_applied && la.text_applied ? (
                <Box sx={{ mt:1.5, p:1, bgcolor:'#0f0f1a', borderRadius:0.5 }}>
                  <Typography variant="caption" sx={{ color:'var(--ac1, #7c4dff)', display:'block', mb:0.5 }}>🎲 Останній рандом (last applied):</Typography>
                  <Swatch rgbArr={la.bg_applied} label="🎨 Фон ±15" />
                  <Swatch rgbArr={la.text_applied} label="✏️ Текст ±15" />
                  <Typography variant="caption" sx={{ color:'#aaa', display:'block', mt:0.5 }}>
                    ↕️ margin_offset: {la.margin_offset != null ? Number(la.margin_offset).toFixed(2) : '—'}% (range ±1%)
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color:'#888', display:'block', mt:1, fontStyle:'italic' }}>(ще не було processing — рандом активується при першому відео)</Typography>
              )}
            </>
          );
        })() : (
          <Typography variant="body2" sx={{ color:'#888' }}>Дефолтні налаштування (юзер ще не змінював): червоний фон, білий текст</Typography>
        )}
      </Paper>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('screenProt')} sx={{ bgcolor: '#1a1a2e', border: '1px solid #f44336', p: 2, mb: 3, maxWidth: 400, order: ord('screenProt') }}>
        <DragHandle id="screenProt"/>
        <Typography variant="subtitle1" sx={{ color: '#f44336', mb: 1 }}>🛡 Захист від скріну</Typography>
        <Box sx={{ display:'flex', gap:1, alignItems:'center', flexWrap:'wrap' }}>
          <Button size="small" variant={userScreenProt===true?"contained":"outlined"} onClick={()=>handleScreenProt(true)}
            sx={{ bgcolor: userScreenProt===true?'#4caf50':'transparent', borderColor:'#4caf50' }}>🔒 УВІМК</Button>
          <Button size="small" variant={userScreenProt===false?"contained":"outlined"} onClick={()=>handleScreenProt(false)}
            sx={{ bgcolor: userScreenProt===false?'#f44336':'transparent', borderColor:'#f44336' }}>🔓 ВИМК</Button>
        </Box>
      </Paper>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('tokens')} sx={{ bgcolor: '#0d1a18', border: '1px solid #00e5a066', p: 2, mb: 3, maxWidth: 400, order: ord('tokens'), borderRadius: 2 }}>
        <DragHandle id="tokens"/>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
          <Typography variant="subtitle1" sx={{ color: '#00e5a0', flex:1 }}>🎟 Токени для спецефектів v2</Typography>
          <Typography sx={{ color:'#7ffcd3', fontWeight:'bold', fontSize:20, lineHeight:1 }}>
            {tokBal === null ? '…' : tokBal}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color:'#8fa3b8', display:'block', mb:1.2, lineHeight:1.5 }}>
          Ними юзер оплачує <b>переклад озвучки</b> й <b>посилення відео</b> у розділі
          «Зі спецефектами». Підписку НЕ подовжують, на ліміт відео/день не впливають
          і не згорають. Юзер докуповує їх сам — тут можна нарахувати вручну
          (бонус, компенсація) або зняти.
        </Typography>
        <Box sx={{ display:'flex', gap:0.5, flexWrap:'wrap', mb:1 }}>
          {[50, 100, 300, -10].map(n => (
            <Button key={n} size="small" onClick={() => grantTokens(data?.user?.telegram_id, n, 'швидка кнопка')}
              sx={{ minWidth:0, px:1.2, fontSize:12, border:'1px solid ' + (n>0?'#00e5a055':'#ff7a5955'),
                    color: n>0?'#7ffcd3':'#ff7a59' }}>
              {n>0?'+':''}{n}
            </Button>
          ))}
        </Box>
        <Box sx={{ display:'flex', gap:1, mb:1 }}>
          <TextField size="small" type="number" placeholder="скільки" value={tokAmt}
            onChange={e=>setTokAmt(e.target.value)}
            sx={{ width:110, input:{color:'#e2e8f0'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#00e5a044'} }} />
          <TextField size="small" placeholder="за що (примітка)" value={tokNote}
            onChange={e=>setTokNote(e.target.value)}
            sx={{ flex:1, input:{color:'#e2e8f0'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#00e5a044'} }} />
          <Button size="small" variant="contained" onClick={()=>grantTokens(data?.user?.telegram_id, tokAmt, tokNote)}
            sx={{ bgcolor:'#00e5a0', color:'#06231a', fontWeight:700, whiteSpace:'nowrap' }}>ОК</Button>
        </Box>
        <Typography variant="caption" sx={{ color:'#64748b', display:'block' }}>
          Відʼємне число — зняти. Нижче нуля баланс не піде.
        </Typography>
        <Box sx={{ mt:1.5, pt:1.2, borderTop:'1px solid #00e5a022' }}>
          <Typography sx={{ color:'#7ffcd3', fontSize:13, fontWeight:'bold', mb:0.3 }}>
            💎 Якісна озвучка (голос самого юзера)
          </Typography>
          <Typography variant="caption" sx={{ color:'#8fa3b8', display:'block', mb:1, lineHeight:1.5 }}>
            Перший запуск створює клон голосу на Replicate — <b style={{color:'#ff7a59'}}>$3 з нашого рахунку</b>,
            один раз на юзера. Тому вимкнено всім за замовчуванням: вмикайте тим,
            хто вже платить. Безкоштовна озвучка диктором доступна всім і без цього.
          </Typography>
          <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
            <Button size="small" variant={proDub===true?'contained':'outlined'}
              onClick={()=>saveProDub(data?.user?.telegram_id, true)}
              sx={{ bgcolor: proDub===true?'#00e5a0':'transparent', color: proDub===true?'#06231a':'#00e5a0',
                    borderColor:'#00e5a0', fontWeight:700 }}>💎 ДОЗВОЛИТИ</Button>
            <Button size="small" variant={proDub===false?'contained':'outlined'}
              onClick={()=>saveProDub(data?.user?.telegram_id, false)}
              sx={{ bgcolor: proDub===false?'#ff7a59':'transparent', color: proDub===false?'#2a0f08':'#ff7a59',
                    borderColor:'#ff7a59', fontWeight:700 }}>🔒 ЗАБОРОНИТИ</Button>
            {proDub===null && <Typography variant="caption" sx={{ color:'#666' }}>завантаження…</Typography>}
          </Box>
        </Box>
        {tokMsg && <Typography variant="caption" sx={{ color: tokMsg.startsWith('❌') ? '#f44' : '#4caf50', display:'block', mt:0.8 }}>{tokMsg}</Typography>}
        {tokLog.length > 0 && (
          <Box sx={{ mt:1.2, borderTop:'1px solid #00e5a022', pt:1 }}>
            {tokLog.slice(0,5).map((r,i) => (
              <Typography key={i} variant="caption" sx={{ color:'#8fa3b8', display:'block', fontSize:11.5 }}>
                {String(r.created_at||'').slice(0,16).replace('T',' ')} ·{' '}
                <b style={{ color: r.delta>0?'#7ffcd3':'#ff7a59' }}>{r.delta>0?'+':''}{r.delta}</b>
                {' '}→ {r.balance_after} · {r.reason}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('eruda')} sx={{ bgcolor: '#1a1a2e', border: '1px solid #2196f3', p: 2, mb: 3, maxWidth: 400, order: ord('eruda') }}>
        <DragHandle id="eruda"/>
        <Typography variant="subtitle1" sx={{ color: '#2196f3', mb: 1 }}>🐞 Eruda консоль (debug)</Typography>
        <Typography variant="caption" sx={{ color:'#888', display:'block', mb:1 }}>Глобально вимкнена у всіх. Увімкнути показ ⚙️ консолі лише цьому юзеру.</Typography>
        <Box sx={{ display:'flex', gap:1, alignItems:'center', flexWrap:'wrap' }}>
          <Button size="small" variant={erudaEnabled===true?"contained":"outlined"} onClick={()=>handleEruda(true)}
            sx={{ bgcolor: erudaEnabled===true?'#4caf50':'transparent', borderColor:'#4caf50' }}>🐞 УВІМК</Button>
          <Button size="small" variant={erudaEnabled===false?"contained":"outlined"} onClick={()=>handleEruda(false)}
            sx={{ bgcolor: erudaEnabled===false?'#f44336':'transparent', borderColor:'#f44336' }}>🚫 ВИМК</Button>
          {erudaEnabled===null && <Typography variant="caption" sx={{ color:'#666' }}>завантаження…</Typography>}
        </Box>
      </Paper>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('message')} sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2, mb: 3, maxWidth: 500, order: ord('message') }}>
        <DragHandle id="message"/>
        <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 1 }}>💬 Надіслати повідомлення</Typography>
        <Box sx={{ display:'flex', gap:1, mb:1 }}>
          <Button variant="outlined" component="label" size="small" sx={{ borderColor:'#555', color:'#aaa', whiteSpace:'nowrap' }}>
            📎<input type="file" hidden accept="image/*,video/*" onChange={e=>{setMsgFile(e.target.files[0]); setMsgMediaType(e.target.files[0]?.type.startsWith('video')?'video':'photo');}}/>
          </Button>
          {msgFile && <Typography variant="caption" sx={{ color:'#4caf50', alignSelf:'center' }}>✅ {msgFile.name}</Typography>}
          {msgFile && <Button size="small" onClick={()=>setMsgFile(null)} sx={{ color:'#f44', minWidth:0 }}>✕</Button>}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField fullWidth size="small" placeholder="Текст повідомлення..." value={msgText}
            onChange={e => setMsgText(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && sendMsg()}
            sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}
          />
          <Button variant="contained" onClick={sendMsg} disabled={msgSending} sx={{ bgcolor: 'var(--ac1, #7c4dff)', whiteSpace: 'nowrap' }}>
            {msgSending ? '⏳...' : 'Надіслати'}
          </Button>
        </Box>
        {msgResult && <Typography sx={{ color: msgResult.includes('✅') ? '#4caf50' : '#f44', mt: 1, fontSize: 13 }}>{msgResult}</Typography>}
      </Paper>
      <Paper onDragOver={e=>e.preventDefault()} onDrop={()=>onCardDrop('ava')} sx={{ bgcolor: '#0e0c1a', border: '1px solid var(--ac1, #7c4dff)66', p: 2, mb: 3, maxWidth: 400, order: ord('ava'), borderRadius: 2 }}>
        <DragHandle id="ava"/>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontSize: 20, lineHeight: 1 }}>🤖</Typography>
          <Typography sx={{ color: '#b39ddb', fontWeight: 'bold', fontSize: 14, flex: 1 }}>AVA налаштування</Typography>
          {!avaEdit && (
            <Button size="small" onClick={() => {
              const rh = avaLim?.video_retention_hours ?? 48;
              setAvaForm({ max_img_mb: avaLim?.max_img_mb ?? 5, max_aud_mb: avaLim?.max_aud_mb ?? 10, max_vid_mb: avaLim?.max_vid_mb ?? 100, daily_gen_limit: avaLim?.daily_gen_limit ?? '', ret_days: String(Math.floor(rh / 24)), ret_hours: String(rh % 24) });
              setAvaEdit(true);
            }} sx={{ color: 'var(--ac2, #a78bfa)', fontSize: 11, border: '1px solid var(--ac1, #7c4dff)55', borderRadius: 1, p: '2px 10px', minWidth: 0 }}>
              {avaLim ? '✏️ Ред.' : '+ Задати'}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8, mb: 1.5 }}>
          {(() => {
            const rh = avaLim?.video_retention_hours ?? 48;
            const rd = Math.floor(rh / 24), rhr = rh % 24;
            const retStr = rd > 0 ? (rd + 'д' + (rhr > 0 ? ' ' + rhr + 'г' : '')) : rh + 'г';
            return [['🖼', 'Фото', (avaLim?.max_img_mb ?? 5) + ' MB'], ['🎙', 'Аудіо', (avaLim?.max_aud_mb ?? 10) + ' MB'], ['🎥', 'Відео', (avaLim?.max_vid_mb ?? 100) + ' MB'], ['⚡', 'Ген/день', avaLim?.daily_gen_limit ? String(avaLim.daily_gen_limit) : '∞'], ['🕐', 'Зберігання', retStr]].map(([icon, label, val]) => (
              <Box key={label} sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.10)', borderRadius: 1, p: '5px 10px', border: '1px solid rgba(var(--ac1rgb, 124, 77, 255),0.22)' }}>
                <Typography sx={{ color: '#64748b', fontSize: 10 }}>{icon} {label}</Typography>
                <Typography sx={{ color: '#e2e8f0', fontSize: 13, fontWeight: 'bold' }}>{val}</Typography>
              </Box>
            ));
          })()}
        </Box>
        {!avaLim && <Typography sx={{ color: '#475569', fontSize: 11, mb: 1 }}>Юзер на дефолтних лімітах</Typography>}
        {avaEdit && (
          <Box sx={{ borderTop: '1px solid var(--ac1, #7c4dff)33', pt: 1.5 }}>
            {[['max_img_mb','🖼 Фото MB','number'],['max_aud_mb','🎙 Аудіо MB','number'],['max_vid_mb','🎥 Відео MB','number'],['daily_gen_limit','⚡ Ген/день (∞=порожньо)','number']].map(([field, label, type]) => (
              <TextField key={field} size="small" label={label} type={type} value={avaForm[field] ?? ''}
                onChange={e => setAvaForm(p => ({...p, [field]: e.target.value}))}
                sx={{ width: '100%', mb: 1, input: { color: '#e2e8f0' }, label: { color: 'var(--ac2, #a78bfa)' }, '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--ac1, #7c4dff)44' }, '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--ac1, #7c4dff)' } }} />
            ))}
            <Typography sx={{ color: 'var(--ac2, #a78bfa)', fontSize: 11, mb: 0.5 }}>🕐 Зберігання відео</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.8 }}>
              {[[1,0,'1д'],[2,0,'2д'],[7,0,'7д'],[14,0,'14д'],[30,0,'30д'],[0,12,'12г'],[0,6,'6г']].map(([d,h,label]) => (
                <Button key={label} size="small" onClick={() => setAvaForm(p => ({...p, ret_days: String(d), ret_hours: String(h)}))}
                  sx={{ fontSize: 11, py: 0.2, px: 1, minWidth: 0, border: '1px solid var(--ac1, #7c4dff)55', color: 'var(--ac2, #a78bfa)', bgcolor: (parseInt(avaForm.ret_days||0)===d && parseInt(avaForm.ret_hours||0)===h) ? 'rgba(var(--ac1rgb, 124, 77, 255),0.25)' : 'transparent' }}>{label}</Button>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" label="Дні" type="number" value={avaForm.ret_days ?? '0'}
                onChange={e => setAvaForm(p => ({...p, ret_days: e.target.value}))}
                sx={{ flex: 1, input: { color: '#e2e8f0' }, label: { color: 'var(--ac2, #a78bfa)' }, '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--ac1, #7c4dff)44' } }} />
              <TextField size="small" label="Години" type="number" value={avaForm.ret_hours ?? '0'}
                onChange={e => setAvaForm(p => ({...p, ret_hours: e.target.value}))}
                sx={{ flex: 1, input: { color: '#e2e8f0' }, label: { color: 'var(--ac2, #a78bfa)' }, '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--ac1, #7c4dff)44' } }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button size="small" variant="contained" sx={{ bgcolor: 'var(--ac1, #7c4dff)', flex: 1, '&:hover': { bgcolor: '#651fff' } }}
                onClick={async () => {
                  try {
                    const saved = {
                      max_img_mb: parseInt(avaForm.max_img_mb) || 5,
                      max_aud_mb: parseInt(avaForm.max_aud_mb) || 10,
                      max_vid_mb: parseInt(avaForm.max_vid_mb) || 100,
                      daily_gen_limit: avaForm.daily_gen_limit ? parseInt(avaForm.daily_gen_limit) : null,
                      video_retention_hours: (parseInt(avaForm.ret_days || 0) * 24) + (parseInt(avaForm.ret_hours || 0)) || 48,
                    };
                    await api.put('/miniapp/avatar/user-limits/' + u.telegram_id, { username: u.telegram_username || '', note: '', ...saved });
                    setAvaLim(prev => ({ ...(prev || {}), tg_id: u.telegram_id, ...saved }));
                    setAvaEdit(false);
                    setAvaMsg('✅ Збережено');
                    setTimeout(() => setAvaMsg(''), 3000);
                  } catch(e) { setAvaMsg('❌ ' + (e.response?.data?.detail || e.message || 'Помилка')); }
                }}>✓ Зберегти</Button>
              <Button size="small" sx={{ color: '#64748b', border: '1px solid #334155', borderRadius: 1 }} onClick={() => setAvaEdit(false)}>Скасувати</Button>
            </Box>
          </Box>
        )}
        {avaMsg && <Typography sx={{ color: avaMsg.startsWith('✅') ? '#4caf50' : '#f44', fontSize: 11, mt: 0.5 }}>{avaMsg}</Typography>}
      </Paper>
      </Box>

      <Box sx={{ display:"flex", gap:1, mb:2 }}>
        {["activity","chat"].map(tab => (
          <Box key={tab} onClick={() => setActiveTab(tab)}
            sx={{ px:2, py:0.8, borderRadius:1, cursor:"pointer", fontSize:13,
              bgcolor: activeTab===tab ? "var(--ac1, #7c4dff)" : "rgba(26,26,46,0.8)",
              color: activeTab===tab ? "#fff" : "#aaa" }}>
            {tab==="activity" ? "📋 Активність" : "💬 Чат"}
          </Box>
        ))}
      </Box>
      {activeTab === "chat" && <Box sx={{ bgcolor:"rgba(26,26,46,0.5)", borderRadius:2, p:1 }}><ChatTab key={chatKey} userId={data?.user?.id}/></Box>}
      {activeTab === "activity" && <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2 }}>

            <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>📅 Дати</Typography>
            {dates.length === 0 ? <Typography sx={{ color: '#666', fontSize: 13 }}>Немає</Typography> :
              <List dense disablePadding>
                {dates.map(d => (
                  <ListItem key={d.date} disablePadding sx={{ borderRadius: 1, mb: 0.5,
                    bgcolor: selectedDate===d.date?'rgba(var(--ac1rgb, 124, 77, 255),0.2)':'transparent',
                    cursor:'pointer','&:hover':{bgcolor:'rgba(var(--ac1rgb, 124, 77, 255),0.1)'}}}
                    onClick={() => loadActivities(d.date)}
                    secondaryAction={(d.prod || d.staging) ? <Tooltip title="Видалити"><IconButton size="small" onClick={e=>{e.stopPropagation();handleDeleteDate(d.date, d.prod ? 'prod' : 'staging');}} sx={{color:'#f44336'}}><DeleteIcon fontSize="small"/></IconButton></Tooltip> : null}>
                    <ListItemText primary={(d.staging ? '🧪 ' : '') + d.date + (d.device ? ' 📲' : '')} secondary={'$'+(costData?.by_date?.find(c=>c.date===d.date)?.cost||0).toFixed(4)} primaryTypographyProps={{style:{color: d.staging && d.prod===false ? '#ff9800' : '#fff',fontSize:13}}} secondaryTypographyProps={{style:{color:'#f44336',fontSize:11}}}/>
                  </ListItem>
                ))}
              </List>
            }
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2 }}>
            <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>
              {selectedDate ? (() => {
                const totalVersions = sessions.reduce((sum, s) => {
                  const pc = s.actions?.find(a => a.action_type === 'processing_completed');
                  const vers = pc?.action_data?.versions || pc?.action_data?.uniq_versions?.split('|').length || 1;
                  return sum + parseInt(vers);
                }, 0);
                return '📋 ' + selectedDate + ' (' + sessions.length + ' відео | 🎬 ' + totalVersions + ' версій)';
              })() : '📋 Оберіть дату'}
            </Typography>
            {actLoading ? <CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }} size={24}/> :
              sessions.length === 0 ? <Typography sx={{ color: '#666' }}>Немає даних</Typography> :
              sessions.map((s, i) => <SessionCard key={i} session={s} idx={i} onDelete={deleteSession} simRows={simRows} vTask={(vLedger && vLedger.by_task) || (vlToday && vlToday.by_task)}/>)
            }
            {selectedDate && <VersionsExplain v={vLedger && vLedger.day === selectedDate ? vLedger : null} />}
            {/* [D-571] заміри ПК (Autopilot Studio, D-580: order_salt = -id пристрою) - сесій в «Активності» ПК не має, тому окремо за цей день */}
            {selectedDate && (() => {
              const used = new Set();   // [D-731] заміри, що вже в картках обробок застосунків, тут не повторюємо
              sessions.forEach(s => { const p = s.actions.find(a => a.action_type === 'processing_started'); ((p && p.action_data && p.action_data._sim_ids) || []).forEach(x => used.add(x)); });
              const pcs = (simRows || []).filter(r => Number(r.order_salt) < 0 && !used.has(r.id) && String(r.created_at || '').slice(0, 10) === selectedDate);
              if (!pcs.length) return null;
              return (
                <Box sx={{ mt: 1, p: 1.2, border: '1px solid #2a3a4e', borderRadius: 1, bgcolor: '#0f0f1a' }}>
                  <Typography variant="caption" sx={{ color: '#9a86ff', fontWeight: 'bold', display: 'block', mb: 0.5 }}>🖥 ПК (Autopilot Studio) · 🔬 схожість версій з оригіналом</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, fontSize: 12 }}>
                    {pcs.slice().reverse().map(r => <span key={r.id}><span style={{ color: '#777' }}>{String(r.created_at).slice(11, 16)}</span> <b style={{ color: '#f5c84c' }}>v{r.ver}</b> <SimCell s={r} /></span>)}
                  </Box>
                </Box>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>}
      {/* [D-603] вікно «🧮 як пораховано» з плитки «Відео сьогодні» */}
      <Dialog open={vlOpen} onClose={() => setVlOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid var(--ac1, #7c4dff)' } }}>
        <DialogTitle sx={{ color: '#fff', pb: 1 }}>🧮 Відео сьогодні - як пораховано</DialogTitle>
        <DialogContent>{vlToday ? <VersionsExplain v={vlToday} title={'Доба ' + vlToday.day + ' (за Києвом)'} /> : <Typography sx={{ color: '#777' }}>Немає даних журналу</Typography>}</DialogContent>
        <DialogActions><Button onClick={() => setVlOpen(false)}>Закрити</Button></DialogActions>
      </Dialog>
      {/* [D-608] вікно «🧮 як пораховано» строки й гроші: вкладки кнопками, без довгих списків */}
      <Dialog open={!!howTab} onClose={() => setHowTab(null)} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid var(--ac1, #7c4dff)' } }}>
        <DialogTitle sx={{ color: '#fff', pb: 1 }}>🧮 Як пораховано · @{u.telegram_username || u.telegram_id}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {[['subscription_end', '📅 Підписка'], ['trial_end', '🎟 Тріал'], ['pcapp_until', '🖥 ПК'], ['money', '💶 Гроші']].map(([k, l]) =>
              <Chip key={k} label={l} clickable onClick={() => setHowTab(k)}
                sx={{ bgcolor: howTab === k ? 'rgba(var(--ac1rgb, 124, 77, 255),.45)' : 'rgba(255,255,255,.07)', color: howTab === k ? '#fff' : '#aaa' }} />)}
          </Box>
          {howTab === 'money' ? <MoneyExplain m={howMoney} /> : (howTab ? <AccessExplain a={howAccess} field={howTab} /> : null)}
        </DialogContent>
        <DialogActions><Button onClick={() => setHowTab(null)}>Закрити</Button></DialogActions>
      </Dialog>
      {/* Receipts modal for this user */}
      <Dialog open={receiptsOpen} onClose={() => setReceiptsOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid var(--ac1, #7c4dff)' } }}>
        <DialogTitle sx={{ color: '#fff', pb: 1 }}>🧾 Квитанції @{u.telegram_username || '?'}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {userReceipts.length === 0
            ? <Typography sx={{ color: '#555', p: 3, textAlign: 'center' }}>Немає квитанцій</Typography>
            : userReceipts.map(r => (
                <Box key={r.id} sx={{ py: 1.2, px: 2, borderBottom: '1px solid #2a2a3e' }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    {r.created_at ? r.created_at.substring(0, 16).replace('T', ' ') : '—'}
                  </Typography>
                  {r.package_name && (
                    <Typography variant="body2" sx={{ color: '#FFD700' }}>🎫 {r.package_name} • €{r.amount || 0}</Typography>
                  )}
                  {r.confirmed_by === 'mono_auto' && (
                    <Typography variant="caption" sx={{ color: '#2196f3', display: 'block' }}>💳 Картка (Monobank)</Typography>
                  )}
                  <Typography variant="caption" sx={{ color: r.payment_confirmed ? '#4caf50' : '#f44336' }}>
                    {r.payment_confirmed ? '✅ Підтверджено' : '⏳ Очікує'}
                    {r.confirmed_by && r.confirmed_by !== 'mono_auto' ? ' (' + r.confirmed_by + ')' : ''}
                  </Typography>
                </Box>
              ))
          }
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#1a1a2e' }}>
          <Button onClick={() => setReceiptsOpen(false)} sx={{ color: '#aaa' }}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
